import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";

/**
 * ForecastDashboard.js
 * - Single-file version of api.ts + App.tsx (JS only)
 * - Admin-gated (UserID=admin)
 * - Calls staged overlay backend at: assent.ogpumper.net/api/overlay_staged.php
 *
 * Dependencies:
 *   npm i echarts echarts-for-react
 */

// ---------- API base (IMPORTANT: avoid mixed-content on https) ----------
function resolveApiBase() {
  // Explicit production base (nginx HTTPS)
  const defaultBase = "https://assent.ogpumper.net";

  // Optional overrides:
  // 1) window.__FORECAST_API_BASE__ = "https://custom-host"
  // 2) process.env.REACT_APP_FORECAST_API_BASE (CRA-style)
  const win = window.__FORECAST_API_BASE__;
  const env =
    (typeof process !== "undefined" &&
      process?.env?.REACT_APP_FORECAST_API_BASE) ||
    "";

  return String(win || env || defaultBase).trim();
}

// ---------- Constants & helpers ----------
const STREAMS = ["Oil", "Gas", "Water"];
const COLORS = {
  Oil: "#2563eb",
  Gas: "#ef4444",
  Water: "#10b981",
};

const fmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const fmt0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const pct = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 1,
});
const iso = (d) => d.toISOString().slice(0, 10);

function parseISO(s) {
  return new Date(s + (s && s.length === 10 ? "T00:00:00Z" : ""));
}
function definedNum(n) {
  return Number.isFinite(n);
}
function toPair(p) {
  return definedNum(p?.q) ? [p.date, p.q] : null;
}
function clamp(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}
function clamp01(n) {
  return clamp(n, 0, 1);
}
function daysBetweenISO(a, b) {
  if (!a || !b) return null;
  const ta = +parseISO(a);
  const tb = +parseISO(b);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null;
  return Math.round((tb - ta) / 86400000);
}
function money(n, digits = 0) {
  if (!definedNum(n)) return "—";
  const nf = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits,
  });
  return nf.format(n);
}
function signColor(v, dark) {
  if (!definedNum(v)) return dark ? "#9ca3af" : "#6b7280";
  return v >= 0 ? (dark ? "#34d399" : "#059669") : dark ? "#f87171" : "#b91c1c";
}

function movingAverage(points, window = 7) {
  const arr = (points || [])
    .filter((p) => definedNum(p?.q))
    .map((p) => ({ t: +parseISO(p.date), d: p.date, v: p.q }))
    .sort((a, b) => a.t - b.t);

  const out = [];
  const q = [];
  for (let i = 0; i < arr.length; i++) {
    q.push(arr[i].v);
    if (q.length > window) q.shift();
    const avg = q.reduce((s, x) => s + x, 0) / q.length;
    out.push([arr[i].d, avg]);
  }
  return out;
}

function outlierDates(points) {
  const vals = (points || [])
    .filter((p) => definedNum(p?.q))
    .map((p) => p.q)
    .sort((a, b) => a - b);

  if (vals.length < 12) return new Set();
  const q1 = vals[Math.floor(vals.length * 0.25)];
  const q3 = vals[Math.floor(vals.length * 0.75)];
  const iqr = q3 - q1;
  const lo = q1 - 1.5 * iqr;
  const hi = q3 + 1.5 * iqr;

  return new Set(
    (points || [])
      .filter((p) => definedNum(p?.q) && (p.q < lo || p.q > hi))
      .map((p) => p.date),
  );
}

function latest(points) {
  if (!points || !points.length) return undefined;
  return [...points]
    .filter((p) => definedNum(p?.q))
    .sort((a, b) => +parseISO(b.date) - +parseISO(a.date))[0];
}

function averageOfLast(points, days = 7) {
  const sorted = [...(points || [])]
    .filter((p) => definedNum(p?.q))
    .sort((a, b) => +parseISO(b.date) - +parseISO(a.date));
  const tail = sorted.slice(0, days).map((p) => p.q);
  if (!tail.length) return null;
  return tail.reduce((s, x) => s + x, 0) / tail.length;
}

function csvEscape(s) {
  return /[",\n]/.test(s) ? `"${String(s).replace(/"/g, '""')}"` : String(s);
}

function hexToRgba(hex, alpha = 1) {
  const h = String(hex || "").replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function cumulative(series) {
  let acc = 0;
  return [...(series || [])]
    .sort((a, b) => +parseISO(a.date) - +parseISO(b.date))
    .map((p) => {
      const v = definedNum(p?.q) ? p.q : 0;
      acc += v;
      return { date: p.date, q: acc };
    });
}

function isQuantilePointArray(entry) {
  if (!Array.isArray(entry) || entry.length === 0) return false;
  const x = entry[0];
  return (
    x &&
    typeof x === "object" &&
    "date" in x &&
    ("p50" in x || "p10" in x || "p90" in x || "mean" in x || "median" in x)
  );
}

function gatherAllDates(actual, forecast) {
  const dates = new Set();
  Object.values(actual || {}).forEach((arr) =>
    arr?.forEach?.((p) => dates.add(p.date)),
  );

  for (const s of STREAMS) {
    const f = forecast?.[s];
    if (!f) continue;

    if (Array.isArray(f)) {
      if (isQuantilePointArray(f)) f.forEach((p) => dates.add(p.date));
      else f.forEach((p) => dates.add(p.date));
    } else if (typeof f === "object") {
      (f.p10 || []).forEach((p) => dates.add(p.date));
      (f.p50 || f.median || f.mean || []).forEach((p) => dates.add(p.date));
      (f.p90 || []).forEach((p) => dates.add(p.date));
    }
  }

  return [...dates].sort((a, b) => +parseISO(a) - +parseISO(b));
}

function datesIntersection(a, b) {
  const mb = new Map((b || []).map(([d, v]) => [d, v]));
  const outA = [];
  const outB = [];
  for (const [d, va] of a || []) {
    if (mb.has(d)) {
      outA.push([d, va]);
      outB.push([d, mb.get(d)]);
    }
  }
  return { a: outA, b: outB };
}

function extractForecast(entry) {
  const empty = { mid: [], p10: [], p90: [], hasBand: false };
  if (!entry) return empty;

  // A) Array of Points [{date,q}]
  if (Array.isArray(entry) && !isQuantilePointArray(entry)) {
    return { ...empty, mid: entry.map(toPair).filter(Boolean) };
  }

  // B) quantile-per-point array [{date,p10,p50,p90}]
  if (Array.isArray(entry) && isQuantilePointArray(entry)) {
    const mid = entry
      .map((p) =>
        definedNum(p.p50 ?? p.median ?? p.mean)
          ? [p.date, Number(p.p50 ?? p.median ?? p.mean)]
          : null,
      )
      .filter(Boolean);
    const p10 = entry
      .map((p) => (definedNum(p.p10) ? [p.date, Number(p.p10)] : null))
      .filter(Boolean);
    const p90 = entry
      .map((p) => (definedNum(p.p90) ? [p.date, Number(p.p90)] : null))
      .filter(Boolean);
    return { mid, p10, p90, hasBand: p10.length > 0 && p90.length > 0 };
  }

  // C) bands-as-arrays { p10: Point[], p50: Point[], p90: Point[] }
  const obj = entry || {};
  const midSrc = obj.p50 || obj.median || obj.mean || [];
  const mid = (midSrc || []).map(toPair).filter(Boolean);
  const p10 = (obj.p10 || []).map(toPair).filter(Boolean);
  const p90 = (obj.p90 || []).map(toPair).filter(Boolean);
  return { mid, p10, p90, hasBand: p10.length > 0 && p90.length > 0 };
}

function withUnits(val, unit) {
  if (!definedNum(val)) return "—";
  return unit ? `${fmt.format(val)} ${unit}` : fmt.format(val);
}

function normalizeTagsParam(raw) {
  const v = String(raw || "").trim();
  if (!v) return [];
  const low = v.toLowerCase();
  if (low === "all" || low === "~all~") return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** CSV export that understands quantile-per-point arrays */
function toCSV(resp) {
  const allDates = new Set();

  STREAMS.forEach((s) => {
    (resp?.actual?.[s] || []).forEach((p) => allDates.add(p.date));
    const f = resp?.forecast?.[s];
    if (!f) return;

    if (Array.isArray(f)) {
      if (isQuantilePointArray(f)) f.forEach((p) => allDates.add(p.date));
      else f.forEach((p) => allDates.add(p.date));
    } else if (typeof f === "object") {
      (f.p10 || []).forEach((p) => allDates.add(p.date));
      (f.p50 || f.median || f.mean || []).forEach((p) => allDates.add(p.date));
      (f.p90 || []).forEach((p) => allDates.add(p.date));
    }
  });

  const dates = [...allDates].sort((a, b) => +parseISO(a) - +parseISO(b));
  const header = [
    "date",
    ...STREAMS.flatMap((s) => [`${s} Actual`, `${s} Forecast(p50)`]),
  ];
  const rows = [header.join(",")];

  for (const d of dates) {
    const row = [d];
    for (const s of STREAMS) {
      const a = (resp?.actual?.[s] || []).find((p) => p.date === d)?.q ?? "";

      const fRaw = resp?.forecast?.[s];
      let f = "";
      if (Array.isArray(fRaw)) {
        if (isQuantilePointArray(fRaw)) {
          const hit = fRaw.find((p) => p.date === d);
          if (hit) f = hit.p50 ?? hit.median ?? hit.mean ?? "";
        } else {
          f = (fRaw || []).find((p) => p.date === d)?.q ?? "";
        }
      } else if (fRaw && typeof fRaw === "object") {
        const p50 = fRaw.p50 || fRaw.median || fRaw.mean || [];
        f = (p50 || []).find((p) => p.date === d)?.q ?? "";
      }

      row.push(a, f);
    }
    rows.push(
      row.map((x) => (typeof x === "string" ? csvEscape(x) : x)).join(","),
    );
  }

  return rows.join("\n");
}

// ---------- API (collapsed from api.ts) ----------
function buildOverlayUrl(params) {
  const base = resolveApiBase();

  // FIX: correct staged endpoint path
  // was: /overlay_staged.php
  // now: /api/overlay_staged.php
  const path = "/api/overlay_staged.php";

  const url = new URL(path, base);

  // Allow calling with a lease string (fetch by leaseid)
  const p = typeof params === "string" ? { lease: params } : params || {};

  // Only send what the staged overlay endpoint actually expects
  const allowed = ["lease", "horizon", "streams", "tags"];
  for (const k of allowed) {
    const v = p?.[k];
    if (v === undefined || v === null || v === "") continue;
    url.searchParams.set(k, String(v));
  }

  return url.toString();
}

async function fetchOverlay(params, signal) {
  const url = buildOverlayUrl(params);

  const res = await fetch(url, {
    signal,
    headers: { Accept: "application/json" },
  });

  // Friendlier error body (JSON or text)
  if (!res.ok) {
    let body = "";
    try {
      const ct = res.headers.get("content-type") || "";
      body = ct.includes("application/json")
        ? JSON.stringify(await res.json())
        : await res.text();
    } catch {}
    throw new Error(`Overlay API ${res.status}: ${body || res.statusText}`);
  }

  const json = await res.json();

  // Normalize
  json.actual = json.actual || {};
  json.forecast = json.forecast || {};
  json.anomalies = json.anomalies || {};
  json.metrics = json.metrics || {};
  json.meta = json.meta || {};
  return json;
}

/* --------------------------------------------------------------------
 * ECON LAYER (expanded)
 * - Computes daily cashflow from actual + forecast(p50)
 * - Includes:
 *   prices, diffs, shrink, marketing/transport, WI/NRI, taxes,
 *   LOE (fixed/var/water/chem/electric/overhead), capex timing, abandonment,
 *   downtime factor, discounting (PV / PV10), decision rules
 * - Decision point metrics:
 *   first negative cashflow date, economic limit (rolling avg or consecutive),
 *   payout date, PV(Discount), breakeven oil for PV=0, breakeven oil for min CF>=0,
 *   max fixed LOE for PV=0 (sensitivity), scenario compare (Base/Bear/Bull)
 * -------------------------------------------------------------------- */
function boeFrom(oilBbl, gasMcf) {
  const oil = definedNum(oilBbl) ? oilBbl : 0;
  const gas = definedNum(gasMcf) ? gasMcf : 0;
  return oil + gas / 6.0;
}

function seriesMapActual(actualArr) {
  const m = new Map();
  (actualArr || []).forEach((p) => {
    if (p?.date) m.set(p.date, definedNum(p.q) ? Number(p.q) : null);
  });
  return m;
}

function seriesMapForecastMid(forecastEntry) {
  const { mid } = extractForecast(forecastEntry);
  const m = new Map();
  (mid || []).forEach(([d, v]) => {
    m.set(d, definedNum(v) ? Number(v) : null);
  });
  return m;
}

function computeEffectiveWI(econ) {
  const royalty = clamp01(econ.royalty);
  const wiBase = clamp01(econ.workingInterest);
  const nriOverride = Number(econ.nriOverride || 0);
  if (Number.isFinite(nriOverride) && nriOverride > 0) {
    // Treat as NRI; derive WI ~= NRI/(1-royalty)
    const denom = Math.max(1e-9, 1 - royalty);
    return clamp01(nriOverride / denom);
  }
  return wiBase;
}

function discountFactor(annualRate, daysFromStart) {
  const r = Number(annualRate);
  const t = Number(daysFromStart);
  if (!Number.isFinite(r) || r <= 0) return 1;
  if (!Number.isFinite(t) || t <= 0) return 1;
  return 1 / Math.pow(1 + r, t / 365.0);
}

function econRow({ date, oil, gas, water, econ, capex = 0, startDate }) {
  const uptime = 1 - clamp01(econ.downtimePct);

  const oilUnit = definedNum(econ.oilUnitFactor) ? econ.oilUnitFactor : 1;
  const gasUnit = definedNum(econ.gasUnitFactor) ? econ.gasUnitFactor : 1;
  const watUnit = definedNum(econ.waterUnitFactor) ? econ.waterUnitFactor : 1;

  const oilBbl = (definedNum(oil) ? Number(oil) : 0) * oilUnit * uptime;
  const gasMcf = (definedNum(gas) ? Number(gas) : 0) * gasUnit * uptime;
  const watBbl = (definedNum(water) ? Number(water) : 0) * watUnit * uptime;

  const oilPrice = Number(econ.oilPrice) || 0;
  const gasPrice = Number(econ.gasPrice) || 0;

  const netOilPrice = Math.max(
    0,
    oilPrice -
      (Number(econ.oilDiff) || 0) -
      (Number(econ.oilMarketingPerBbl) || 0) -
      (Number(econ.oilTransportPerBbl) || 0),
  );
  const netGasPrice = Math.max(
    0,
    gasPrice -
      (Number(econ.gasDiff) || 0) -
      (Number(econ.gasMarketingPerMcf) || 0) -
      (Number(econ.gasTransportPerMcf) || 0),
  );

  const salesGasMcf = gasMcf * (1 - clamp01(econ.gasShrink));
  const grossOil = oilBbl * netOilPrice;
  const grossGas = salesGasMcf * netGasPrice;
  const grossRev = grossOil + grossGas;

  const royaltyRate = clamp01(econ.royalty);
  const wi = computeEffectiveWI(econ);

  const royalty = grossRev * royaltyRate;

  // Taxes (assume borne on WI share of post-royalty revenue)
  const oilSev = clamp01(econ.oilSevTax);
  const gasSev = clamp01(econ.gasSevTax);
  const sevTax = grossOil * oilSev + grossGas * gasSev;

  const adVal = grossRev * clamp01(econ.adValoremTax);

  const revAfterRoyaltyTaxes = grossRev - royalty - sevTax - adVal;

  // Owner revenue on WI share
  const ownerRev = revAfterRoyaltyTaxes * wi;

  const boe = boeFrom(oilBbl, gasMcf);

  // Opex (assume WI share of costs)
  const fixedLoeDaily = (Number(econ.fixedLoePerMonth) || 0) / 30.0;
  const baseMaintDaily = (Number(econ.baseMaintPerMonth) || 0) / 30.0;
  const overheadDaily = (Number(econ.overheadPerMonth) || 0) / 30.0;

  const varLoe = boe * (Number(econ.varLoePerBoe) || 0);
  const waterDisp = watBbl * (Number(econ.waterDisposalPerBbl) || 0);
  const chemicals = boe * (Number(econ.chemicalsPerBoe) || 0);
  const electricity = boe * (Number(econ.electricityPerBoe) || 0);

  const lowRateExtraDaily =
    boe < (Number(econ.lowRateBoePerDayThreshold) || 0)
      ? (Number(econ.lowRateExtraMaintPerMonth) || 0) / 30.0
      : 0;

  const opexRaw =
    fixedLoeDaily +
    varLoe +
    waterDisp +
    baseMaintDaily +
    lowRateExtraDaily +
    chemicals +
    electricity +
    overheadDaily;

  const opex = opexRaw * wi;

  const capexOwner = (Number(capex) || 0) * wi;

  const cashflow = ownerRev - opex - capexOwner;

  const tDays = startDate ? (daysBetweenISO(startDate, date) ?? 0) : 0;
  const pv =
    cashflow * discountFactor(Number(econ.discountRateAnnual) || 0, tDays);

  return {
    date,
    oil: oilBbl,
    gas: gasMcf,
    water: watBbl,
    boe,
    netOilPrice,
    netGasPrice,
    grossOil,
    grossGas,
    grossRev,
    royalty,
    sevTax,
    adVal,
    wi,
    ownerRev,
    opex,
    capex: capexOwner,
    cashflow,
    pv,
  };
}

function firstNegative(rows) {
  const hit = (rows || []).find(
    (r) => definedNum(r?.cashflow) && r.cashflow < 0,
  );
  return hit ? hit.date : "";
}

function rollingAvgNegativeDate(rows, windowDays = 14) {
  const w = Math.max(2, Math.min(90, parseInt(windowDays || "14", 10)));
  const arr = (rows || []).filter((r) => r && r.date).slice();
  if (arr.length < w) return "";
  for (let i = w - 1; i < arr.length; i++) {
    let sum = 0;
    let ok = 0;
    for (let j = i - (w - 1); j <= i; j++) {
      const v = arr[j]?.cashflow;
      if (definedNum(v)) {
        sum += v;
        ok += 1;
      }
    }
    if (ok > 0) {
      const avg = sum / ok;
      if (avg < 0) return arr[i].date;
    }
  }
  return "";
}

function consecutiveNegativeDate(rows, consecutiveDays = 14) {
  const need = Math.max(2, Math.min(90, parseInt(consecutiveDays || "14", 10)));
  let run = 0;
  for (const r of rows || []) {
    if (definedNum(r?.cashflow) && r.cashflow < 0) run += 1;
    else run = 0;
    if (run >= need) return r.date;
  }
  return "";
}

function payoutDate(rows) {
  // First date cumulative cashflow >= 0 (useful when you include upfront capex)
  let cum = 0;
  for (const r of rows || []) {
    const cf = definedNum(r?.cashflow) ? r.cashflow : 0;
    cum += cf;
    if (cum >= 0) return r.date;
  }
  return "";
}

function sumOf(rows, key) {
  return (rows || []).reduce(
    (s, r) => s + (definedNum(r?.[key]) ? r[key] : 0),
    0,
  );
}

function applyScenario(econ, scenario) {
  const sc = scenario || { oilMult: 1, gasMult: 1, opexMult: 1, name: "Base" };
  const o = { ...econ };

  const oilMult = Number(sc.oilMult) || 1;
  const gasMult = Number(sc.gasMult) || 1;
  const opexMult = Number(sc.opexMult) || 1;

  o.oilPrice = (Number(o.oilPrice) || 0) * oilMult;
  o.gasPrice = (Number(o.gasPrice) || 0) * gasMult;

  // Opex scale (optional but useful for sensitivity)
  o.fixedLoePerMonth = (Number(o.fixedLoePerMonth) || 0) * opexMult;
  o.varLoePerBoe = (Number(o.varLoePerBoe) || 0) * opexMult;
  o.waterDisposalPerBbl = (Number(o.waterDisposalPerBbl) || 0) * opexMult;
  o.baseMaintPerMonth = (Number(o.baseMaintPerMonth) || 0) * opexMult;
  o.lowRateExtraMaintPerMonth =
    (Number(o.lowRateExtraMaintPerMonth) || 0) * opexMult;
  o.chemicalsPerBoe = (Number(o.chemicalsPerBoe) || 0) * opexMult;
  o.electricityPerBoe = (Number(econ.electricityPerBoe) || 0) * opexMult;
  o.overheadPerMonth = (Number(o.overheadPerMonth) || 0) * opexMult;

  return o;
}

function solveBisection({ lo, hi, fn, target = 0, maxIter = 40, eps = 1e-6 }) {
  let a = lo;
  let b = hi;
  let fa = fn(a) - target;
  let fb = fn(b) - target;

  if (!Number.isFinite(fa) || !Number.isFinite(fb)) return null;
  if (Math.abs(fa) < eps) return a;
  if (Math.abs(fb) < eps) return b;

  // If not bracketed, no solution in [lo, hi]
  if (fa * fb > 0) return null;

  for (let i = 0; i < maxIter; i++) {
    const m = (a + b) / 2;
    const fm = fn(m) - target;
    if (!Number.isFinite(fm)) return null;
    if (Math.abs(fm) < eps) return m;
    if (fa * fm <= 0) {
      b = m;
      fb = fm;
    } else {
      a = m;
      fa = fm;
    }
  }
  return (a + b) / 2;
}

function toEconCSV(econResult) {
  const rows = econResult?.rows || [];
  const header = [
    "date",
    "oil",
    "gas",
    "water",
    "boe",
    "grossRev",
    "royalty",
    "sevTax",
    "adVal",
    "wi",
    "ownerRev",
    "opex",
    "capex",
    "cashflow",
    "cumCashflow",
    "pv",
  ];
  const out = [header.join(",")];

  let cum = 0;
  for (const r of rows) {
    const cf = definedNum(r.cashflow) ? r.cashflow : 0;
    cum += cf;

    const line = [
      r.date,
      r.oil,
      r.gas,
      r.water,
      r.boe,
      r.grossRev,
      r.royalty,
      r.sevTax,
      r.adVal,
      r.wi,
      r.ownerRev,
      r.opex,
      r.capex,
      r.cashflow,
      cum,
      r.pv,
    ].map((x) =>
      typeof x === "string" ? csvEscape(x) : definedNum(x) ? String(x) : "",
    );
    out.push(line.join(","));
  }

  return out.join("\n");
}

function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="modalOverlay" role="dialog" aria-modal="true">
      <div className="modalCard">
        <div className="modalHeader">
          <div>
            <div className="modalTitle">{title}</div>
            <div className="modalSub">
              Decision metrics, breakevens, PV, and assumptions
            </div>
          </div>
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modalBody">{children}</div>
        {footer ? <div className="modalFooter">{footer}</div> : null}
      </div>
    </div>
  );
}

// ---------- Component ----------
export default function ForecastDashboard() {
  // Parse initial query once (prevents conditional hooks)
  const init = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);

    const userIdRaw = String(qs.get("UserID") || qs.get("user") || "")
      .trim()
      .toLowerCase();
    const isAdmin = ["admin", "kristie"].includes(
      String(userIdRaw || "")
        .trim()
        .toLowerCase(),
    );

    // Keep pass-through params intact (token/grant/etc.)
    const passThrough = {};
    ["Company", "Name", "UserID", "token", "grant", "App"].forEach((k) => {
      const v = qs.get(k);
      if (v !== null && v !== undefined && v !== "") passThrough[k] = v;
    });

    const companyLabelRaw =
      qs.get("Name") || qs.get("Company") || "Production Forecasts";
    let companyLabel = companyLabelRaw;
    try {
      companyLabel = decodeURIComponent(companyLabelRaw);
    } catch {}

    // Use Company subdomain if provided, otherwise default to the SAME host as the app base.
    const baseHost = (() => {
      try {
        return new URL(resolveApiBase()).origin;
      } catch {
        return "https://assent.ogpumper.net";
      }
    })();
    const companySubdomain = (qs.get("Company") || "").trim().toLowerCase();
    const companyBase = companySubdomain
      ? `https://${companySubdomain}.ogpumper.net`
      : baseHost;

    const LEASES_API = (() => {
      const override = window.__LEASES_API__;
      if (override && String(override).trim()) return String(override).trim();
      return `${companyBase}/api/leases.php?onlyactivelease=1`;
    })();

    const TAGS_API = (() => {
      const override = window.__TAGS_API__;
      if (override && String(override).trim()) return String(override).trim();
      // per your note: pull from /api/usertags.php
      return `${companyBase}/api/usertags.php`;
    })();

    // Optional date params (kept for OGCharts compatibility / deep links)
    const from = String(qs.get("from") || qs.get("From") || "").trim();
    const thru = String(qs.get("thru") || qs.get("Thru") || "").trim();

    // IMPORTANT: NEVER honor "~ALL~" from URL as a selected lease.
    // We treat it as "not provided" so the UI picks the first real lease after options load.
    const leaseRaw = String(qs.get("lease") ?? qs.get("LeaseID") ?? "").trim();
    const leaseSan =
      leaseRaw && leaseRaw.toLowerCase() === "~all~" ? "" : leaseRaw;
    const leaseProvided = Boolean(leaseSan) && leaseSan !== "~ALL~";
    const lease = leaseProvided ? leaseSan : "";

    const horizon = parseInt(qs.get("horizon") || "60", 10);
    const streams = qs.get("streams")?.split(",").filter(Boolean) || [
      "Oil",
      "Gas",
      "Water",
    ];
    const logScale = (qs.get("scale") ?? "log") === "log";
    const dark = qs.get("theme") === "dark";

    const showMA = qs.get("showMA") ? qs.get("showMA") === "true" : true;
    const maWindow = qs.get("maWindow")
      ? Math.max(2, Math.min(60, parseInt(qs.get("maWindow") || "7", 10)))
      : 7;
    const showOutliers = qs.get("outliers")
      ? qs.get("outliers") === "true"
      : false;
    const showBands = qs.get("bands") ? qs.get("bands") === "true" : true;
    const zeroAsNull = qs.get("zeroAsNull")
      ? qs.get("zeroAsNull") === "true"
      : true;
    const cumulativeMode = qs.get("cumulative") ?? "off";

    const selectedTags = normalizeTagsParam(qs.get("tags") ?? "");

    return {
      isAdmin,
      userIdRaw,
      passThrough,
      companyLabel,
      companySubdomain,
      LEASES_API,
      TAGS_API,
      from,
      thru,
      lease,
      leaseProvided,
      horizon,
      streams,
      logScale,
      dark,
      showMA,
      maWindow,
      showOutliers,
      showBands,
      zeroAsNull,
      cumulativeMode,
      selectedTags,
    };
  }, []);

  // Read params with OGCharts fallbacks
  const [lease, setLease] = useState(init.lease);
  const [horizon, setHorizon] = useState(init.horizon);
  const [streams, setStreams] = useState(init.streams);

  const [logScale, setLogScale] = useState(init.logScale);
  const [dark, setDark] = useState(init.dark);

  const [showMA, setShowMA] = useState(init.showMA);
  const [maWindow, setMaWindow] = useState(init.maWindow);
  const [showOutliers, setShowOutliers] = useState(init.showOutliers);
  const [showBands, setShowBands] = useState(init.showBands);
  const [zeroAsNull, setZeroAsNull] = useState(init.zeroAsNull);
  const [cumulativeMode, setCumulativeMode] = useState(init.cumulativeMode);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [latencyMs, setLatencyMs] = useState(null);
  const [runAt, setRunAt] = useState(null);

  // Leases/tags options
  const [leaseOptions, setLeaseOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [ltLoading, setLtLoading] = useState(false);
  const [ltError, setLtError] = useState(null);

  const [selectedTags, setSelectedTags] = useState(init.selectedTags);

  // ECON assumptions (expanded defaults)
  const [econ, setEcon] = useState({
    // Prices & diffs
    oilPrice: 70,
    gasPrice: 3.0,
    oilDiff: 3.0,
    gasDiff: 0.3,
    gasShrink: 0.05,

    // Midstream / marketing / transport
    oilMarketingPerBbl: 0.0,
    gasMarketingPerMcf: 0.0,
    oilTransportPerBbl: 0.0,
    gasTransportPerMcf: 0.0,

    // Ownership & taxes
    royalty: 0.2,
    workingInterest: 1.0,
    nriOverride: 0.0, // optional: if >0, treated as NRI (derives WI)
    oilSevTax: 0.046,
    gasSevTax: 0.075,
    adValoremTax: 0.0,

    // Opex (LOE)
    fixedLoePerMonth: 4500,
    varLoePerBoe: 2.25,
    waterDisposalPerBbl: 1.25,
    chemicalsPerBoe: 0.0,
    electricityPerBoe: 0.0,
    overheadPerMonth: 0.0,
    baseMaintPerMonth: 350,
    lowRateBoePerDayThreshold: 8,
    lowRateExtraMaintPerMonth: 900,

    // Capex timing
    capexUpfront: 0,
    capexDate: "", // default = econ window start
    abandonmentCost: 0,
    abandonmentDate: "", // default = last day in window

    // Uptime factor
    downtimePct: 0.0,

    // Discounting
    discountRateAnnual: 0.1,

    // Units scaling (if needed)
    oilUnitFactor: 1.0,
    gasUnitFactor: 1.0,
    waterUnitFactor: 1.0,

    // Decision rules / window
    unprofitableSoonDays: 60,
    econLimitRollingDays: 14,
    econLimitConsecutiveNegDays: 14,
  });

  const ECON_SCENARIOS = useMemo(
    () => [
      { key: "Base", name: "Base", oilMult: 1.0, gasMult: 1.0, opexMult: 1.0 },
      {
        key: "Bear",
        name: "Bear",
        oilMult: 0.85,
        gasMult: 0.8,
        opexMult: 1.05,
      },
      {
        key: "Bull",
        name: "Bull",
        oilMult: 1.15,
        gasMult: 1.2,
        opexMult: 0.98,
      },
    ],
    [],
  );
  const [econScenarioKey, setEconScenarioKey] = useState("Base");
  const [econModalOpen, setEconModalOpen] = useState(false);
  const [econTab, setEconTab] = useState("summary"); // summary | assumptions | table

  const chartRef = useRef(null);
  const abortRef = useRef(null);
  const didInitialLoadRef = useRef(false);

  const unitsByStream = data?.meta?.units || {};

  // Persist state back to URL without blowing away token/grant/etc.
  useEffect(() => {
    if (!init.isAdmin) return;

    const q = new URLSearchParams(window.location.search);

    // Keep OG params in sync too (so ogCharts doesn’t break)

    // NEVER write "~ALL~" back into URL. Only set LeaseID when we have a real lease.
    if (lease) q.set("LeaseID", lease);
    else q.delete("LeaseID");

    // Component-friendly params
    if (lease) q.set("lease", lease);
    else q.delete("lease");

    q.set("horizon", String(horizon));
    q.set("streams", streams.join(","));
    q.set("scale", logScale ? "log" : "linear");
    q.set("theme", dark ? "dark" : "light");
    q.set("bands", String(showBands));
    q.set("zeroAsNull", String(zeroAsNull));
    q.set("cumulative", cumulativeMode);
    q.set("showMA", String(showMA));
    q.set("maWindow", String(maWindow));
    q.set("outliers", String(showOutliers));

    if (selectedTags.length) q.set("tags", selectedTags.join(","));
    else q.delete("tags");

    // Keep legacy from/thru if already present (optional deep-linking)
    if (init.from) q.set("from", init.from);
    if (init.thru) q.set("thru", init.thru);

    // Ensure pass-through keys are present
    Object.entries(init.passThrough).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });

    // IMPORTANT: no-restricted-globals fix
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${q.toString()}`,
    );
  }, [
    lease,
    horizon,
    streams,
    logScale,
    dark,
    showBands,
    zeroAsNull,
    cumulativeMode,
    showMA,
    maWindow,
    showOutliers,
    selectedTags,
    init,
  ]);

  // Load leases/tags
  useEffect(() => {
    if (!init.isAdmin) return;

    let alive = true;
    const ac = new AbortController();

    setLtLoading(true);
    setLtError(null);

    (async () => {
      try {
        const [leasesRes, tagsRes] = await Promise.all([
          fetch(init.LEASES_API, {
            signal: ac.signal,
            headers: { Accept: "application/json" },
          }),
          fetch(init.TAGS_API, {
            signal: ac.signal,
            headers: { Accept: "application/json" },
          }),
        ]);

        if (!leasesRes.ok) {
          const txt = await leasesRes.text().catch(() => "");
          throw new Error(
            `Leases API ${leasesRes.status}: ${txt || leasesRes.statusText}`,
          );
        }
        if (!tagsRes.ok) {
          const txt = await tagsRes.text().catch(() => "");
          throw new Error(
            `Tags API ${tagsRes.status}: ${txt || tagsRes.statusText}`,
          );
        }

        const leasesJson = await leasesRes.json();
        const tagsJson = await tagsRes.json();
        const rawLeases = Array.isArray(leasesJson?.Leases)
          ? leasesJson.Leases
          : [];

        if (!alive) return;

        const leases = rawLeases
          .map((l) => ({
            LeaseID: String(l?.LeaseID || "").trim(),
            LeaseName: String(l?.LeaseName || l?.LeaseID || "").trim(),
          }))
          .filter((l) => l.LeaseID && l.LeaseID !== "~ALL~");

        // usertags.php is a mixed array (tags + users + purchasers).
        // Keep ONLY objects that look like tags: {TagID, TagDesc}
        const rawTags = Array.isArray(tagsJson) ? tagsJson : [];
        const tags = rawTags
          .filter(
            (x) => x && typeof x === "object" && "TagID" in x && "TagDesc" in x,
          )
          .map((t) => ({
            TagID: String(t.TagID || "").trim(),
            TagDesc: String(t.TagDesc || "").trim(),
          }))
          .filter((t) => t.TagID && t.TagDesc);

        // Dedupe tags by TagID (keep first)
        const seen = new Set();
        const tagOptionsClean = [];
        for (const t of tags) {
          if (seen.has(t.TagID)) continue;
          seen.add(t.TagID);
          tagOptionsClean.push(t);
        }

        setLeaseOptions(leases);
        setTagOptions(tagOptionsClean);

        // If URL lease was missing or "~ALL~", default to first real lease.
        // ALSO: if URL lease was provided but not found in the list, fall back to first.
        setLease((prev) => {
          const prevRaw = String(prev || "").trim();
          const prevIsAll = prevRaw === "~ALL~";
          const hasPrev = Boolean(prevRaw) && !prevIsAll;

          const exists =
            hasPrev && leases.some((x) => String(x.LeaseID) === prevRaw);

          if (exists) return prevRaw;

          const first = leases?.[0]?.LeaseID;
          return first ? String(first) : "";
        });
      } catch (e) {
        if (!alive) return;
        if (e?.name === "AbortError") return;
        setLtError(e?.message || "Failed to load leases/tags");
      } finally {
        if (!alive) return;
        setLtLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [init]);

  // TAGS FIX (continued):
  // If URL provided tag values that are TagDesc (or mixed), reconcile to TagID once options load.
  // TAGS (continued):
  // If URL provided tag values that are TagDesc (or mixed), reconcile to TagID once options load.
  useEffect(() => {
    if (!tagOptions.length) return;

    const ids = new Set(tagOptions.map((t) => String(t.TagID)));
    const descToId = new Map(
      tagOptions.map((t) => [String(t.TagDesc).toLowerCase(), String(t.TagID)]),
    );

    setSelectedTags((prev) => {
      const next = [];

      for (const raw of prev || []) {
        const v = String(raw || "").trim();
        if (!v) continue;

        // already TagID
        if (ids.has(v)) {
          next.push(v);
          continue;
        }

        // legacy: plain TagDesc
        const byDesc = descToId.get(v.toLowerCase());
        if (byDesc) next.push(byDesc);
      }

      return Array.from(new Set(next));
    });
  }, [tagOptions]);

  async function load() {
    if (!init.isAdmin) return;
    if (!lease || lease === "~ALL~") return;

    abortRef.current?.abort?.();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setLatencyMs(null);
    setRunAt(null);

    try {
      const t0 = performance.now();

      const params = {
        lease,
        horizon,
        streams: streams.join(","),
        ...(selectedTags.length ? { tags: selectedTags.join(",") } : {}),

        // optional pass-through (if backend wants it)
        token: init.passThrough.token || "",
        grant: init.passThrough.grant || "",
        UserID: init.passThrough.UserID || "",
        Company: init.passThrough.Company || "",
        Name: init.passThrough.Name || "",
      };

      const res = await fetchOverlay(params, abortRef.current.signal);
      const t1 = performance.now();

      setLatencyMs(Math.round(t1 - t0));
      setRunAt(new Date().toLocaleString());
      setData(res);

      if (res?.meta?.yScale) setLogScale(res.meta.yScale === "log");
    } catch (e) {
      if (e?.name !== "AbortError")
        setError(e?.message || "Failed to load overlay");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!init.isAdmin) return;
    if (didInitialLoadRef.current) return;

    // GUARANTEE: do not run initial load unless we have a real lease (never "~ALL~").
    if (!lease || lease === "~ALL~") return;

    didInitialLoadRef.current = true;
    load(); /* initial */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init.isAdmin, lease]);

  // Reload on parameter changes AFTER initial load
  useEffect(() => {
    if (!init.isAdmin) return;
    if (!didInitialLoadRef.current) return;
    if (!lease || lease === "~ALL~") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lease, horizon, streams, selectedTags]);

  // Chart-ready "actual" (may be cumulative)
  const preparedActual = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const s of STREAMS) {
      const arr = (data.actual?.[s] || []).map((p) => {
        if (zeroAsNull && p.q === 0) return { ...p, q: null };
        return p;
      });
      out[s] = cumulativeMode === "on" ? cumulative(arr) : arr;
    }
    return out;
  }, [data, zeroAsNull, cumulativeMode]);

  // Econ should ALWAYS use daily series (not cumulative)
  const actualForEcon = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const s of STREAMS) {
      const arr = (data.actual?.[s] || []).map((p) => {
        if (zeroAsNull && p.q === 0) return { ...p, q: null };
        return p;
      });
      out[s] = arr;
    }
    return out;
  }, [data, zeroAsNull]);

  const leaseNameById = useMemo(() => {
    const m = new Map();
    leaseOptions.forEach((l) => m.set(l.LeaseID, l.LeaseName));
    return m;
  }, [leaseOptions]);

  const tagDescById = useMemo(() => {
    const m = new Map();
    tagOptions.forEach((t) => m.set(String(t.TagID), t.TagDesc));
    return m;
  }, [tagOptions]);

  function minForYAxis() {
    if (!data) return logScale ? 0.1 : "dataMin";
    const values = [];
    for (const s of STREAMS) {
      (preparedActual[s] || []).forEach(
        (p) => definedNum(p?.q) && values.push(p.q),
      );
      const fEntry = data?.forecast?.[s];
      const { mid, p10, p90 } = extractForecast(fEntry);
      [...mid, ...p10, ...p90].forEach(
        ([, v]) => definedNum(v) && values.push(v),
      );
    }
    const min = Math.min(...values.filter((v) => v > 0));
    if (!Number.isFinite(min)) return logScale ? 0.1 : "dataMin";
    if (!logScale) return Math.max(0, Math.floor(min - 0.1 * Math.abs(min)));
    return Math.max(0.01, min * 0.8);
  }

  const domain = useMemo(() => {
    if (!data) return { min: "", max: "" };
    const all = gatherAllDates(preparedActual, data.forecast);
    return {
      min: all[0] || data?.meta?.forecast_start || "",
      max: all[all.length - 1] || data?.meta?.forecast_end || "",
    };
  }, [data, preparedActual]);

  const cards = useMemo(() => {
    const out = [];
    if (!data) return out;

    for (const s of STREAMS) {
      const lastPt = latest(preparedActual[s] || []);
      const last = lastPt?.q;
      const lastDate = lastPt?.date;
      const avg7 = averageOfLast(preparedActual[s] || [], 7) ?? undefined;
      const delta =
        definedNum(last) && definedNum(avg7) ? (last - avg7) / avg7 : undefined;

      const fEntry = data?.forecast?.[s];
      let next7;
      if (fEntry) {
        const { mid } = extractForecast(fEntry);
        if (mid?.length) {
          const startAfter = data?.meta?.forecast_start || iso(new Date());
          const filtered = mid
            .filter(([d]) => d >= startAfter)
            .slice(0, 7)
            .map(([, v]) => v || 0);
          if (filtered.length)
            next7 = filtered.reduce((a, b) => a + (b || 0), 0);
        }
      }

      out.push({ stream: s, last, lastDate, avg7, delta, next7 });
    }
    return out;
  }, [data, preparedActual]);

  // ECON computed window (forward from Thru for N days) + decision metrics + scenarios
  const econBundle = useMemo(() => {
    if (!data) return null;

    const all = gatherAllDates(actualForEcon, data.forecast);
    if (!all.length) return null;

    const start = data?.meta?.forecast_start || domain.min || iso(new Date());
    const days = Math.max(7, parseInt(econ.unprofitableSoonDays || "60", 10));
    const forward = all.filter((d) => d >= start).slice(0, days);

    const aOil = seriesMapActual(actualForEcon.Oil || []);
    const aGas = seriesMapActual(actualForEcon.Gas || []);
    const aWat = seriesMapActual(actualForEcon.Water || []);

    const fOil = seriesMapForecastMid(data.forecast?.Oil);
    const fGas = seriesMapForecastMid(data.forecast?.Gas);
    const fWat = seriesMapForecastMid(data.forecast?.Water);

    function computeForEcon(econUse) {
      // Capex schedule
      const capexDate = (econUse.capexDate || "").trim() || start;
      const abandonmentDate =
        (econUse.abandonmentDate || "").trim() ||
        forward[forward.length - 1] ||
        start;

      let rows = forward.map((d) => {
        const oil = definedNum(fOil.get(d)) ? fOil.get(d) : aOil.get(d);
        const gas = definedNum(fGas.get(d)) ? fGas.get(d) : aGas.get(d);
        const wat = definedNum(fWat.get(d)) ? fWat.get(d) : aWat.get(d);

        let capex = 0;
        if (definedNum(econUse.capexUpfront) && econUse.capexUpfront !== 0) {
          if (d === capexDate) capex += Number(econUse.capexUpfront) || 0;
        }
        if (
          definedNum(econUse.abandonmentCost) &&
          econUse.abandonmentCost !== 0
        ) {
          if (d === abandonmentDate)
            capex += Number(econUse.abandonmentCost) || 0;
        }

        return econRow({
          date: d,
          oil,
          gas,
          water: wat,
          econ: econUse,
          capex,
          startDate: start,
        });
      });

      // Cumulative & decision metrics
      let cum = 0;
      rows = rows.map((r) => {
        cum += definedNum(r.cashflow) ? r.cashflow : 0;
        return { ...r, cumCashflow: cum };
      });

      const firstNeg = firstNegative(rows);
      const rollNeg = rollingAvgNegativeDate(
        rows,
        econUse.econLimitRollingDays,
      );
      const consNeg = consecutiveNegativeDate(
        rows,
        econUse.econLimitConsecutiveNegDays,
      );

      // Economic limit: earliest of rolling negative avg or consecutive negatives
      const econLimit = [rollNeg, consNeg].filter(Boolean).sort()[0] || "";

      const pv = sumOf(rows, "pv");
      const totalCash = sumOf(rows, "cashflow");
      const totalBoe = sumOf(rows, "boe");
      const totalOpex = sumOf(rows, "opex");
      const netback = totalBoe > 0 ? totalCash / totalBoe : null;
      const loePerBoe = totalBoe > 0 ? totalOpex / totalBoe : null;

      const pay = payoutDate(rows);

      // Decision label
      const soonDays = Math.max(
        7,
        parseInt(econUse.unprofitableSoonDays || "60", 10),
      );
      const dFirstNeg = firstNeg ? daysBetweenISO(start, firstNeg) : null;
      const dLimit = econLimit ? daysBetweenISO(start, econLimit) : null;

      let decision = "Operate";
      let decisionDetail = "Cashflow stays positive under current assumptions.";
      if (
        econLimit &&
        definedNum(dLimit) &&
        dLimit >= 0 &&
        dLimit <= soonDays
      ) {
        decision = "Shut-in candidate";
        decisionDetail = `Economic limit hits in ~${dLimit} days (by rules).`;
      } else if (
        firstNeg &&
        definedNum(dFirstNeg) &&
        dFirstNeg >= 0 &&
        dFirstNeg <= soonDays
      ) {
        decision = "Monitor";
        decisionDetail = `First negative cashflow appears in ~${dFirstNeg} days.`;
      }

      return {
        start,
        days: soonDays,
        rows,
        firstNegDate: firstNeg,
        econLimitDate: econLimit,
        rollingNegDate: rollNeg,
        consecutiveNegDate: consNeg,
        payoutDate: pay,
        pv,
        totalCash,
        netback,
        loePerBoe,
        decision,
        decisionDetail,
      };
    }

    // Compute scenarios
    const scenarios = {};
    for (const sc of ECON_SCENARIOS) {
      const econUse = applyScenario(econ, sc);
      scenarios[sc.key] = {
        key: sc.key,
        name: sc.name,
        config: sc,
        econUse,
        result: computeForEcon(econUse),
      };
    }

    // Selected scenario
    const selected = scenarios[econScenarioKey] || scenarios.Base;

    // Breakevens on selected scenario (PV=0 and min CF>=0)
    const baseEcon = selected.econUse;
    const computePVAtOil = (oilPrice) => {
      const e2 = { ...baseEcon, oilPrice };
      return computeForEcon(e2).pv;
    };
    const computeMinCFAtOil = (oilPrice) => {
      const e2 = { ...baseEcon, oilPrice };
      const res = computeForEcon(e2);
      const mins = res.rows.map((r) =>
        definedNum(r.cashflow) ? r.cashflow : 0,
      );
      return Math.min(...mins);
    };
    const computePVAtFixedLoe = (fixedLoePerMonth) => {
      const e2 = { ...baseEcon, fixedLoePerMonth };
      return computeForEcon(e2).pv;
    };

    const beOilPV0 = solveBisection({
      lo: 0,
      hi: 200,
      fn: computePVAtOil,
      target: 0,
      maxIter: 40,
      eps: 1e-3,
    });

    const beOilMinCF0 = solveBisection({
      lo: 0,
      hi: 250,
      fn: computeMinCFAtOil,
      target: 0,
      maxIter: 40,
      eps: 1e-2,
    });

    const maxFixedLoePV0 = solveBisection({
      lo: 0,
      hi: Math.max(1000, (Number(baseEcon.fixedLoePerMonth) || 0) * 4),
      fn: computePVAtFixedLoe,
      target: 0,
      maxIter: 40,
      eps: 1e-2,
    });

    return {
      scenarios,
      selectedKey: selected.key,
      selectedName: selected.name,
      selectedEcon: baseEcon,
      selectedResult: selected.result,
      breakevens: {
        beOilPV0,
        beOilMinCF0,
        maxFixedLoePV0,
      },
    };
  }, [data, actualForEcon, econ, econScenarioKey, ECON_SCENARIOS, domain.min]);

  const option = useMemo(() => {
    const themeText = dark ? "#e5e7eb" : "#111827";
    const series = [];

    if (data) {
      const allF = gatherAllDates({}, data.forecast);
      const forecastStart = allF[0];
      const lastDate = domain.max;

      if (forecastStart && lastDate) {
        series.push({
          name: "Forecast Window",
          type: "line",
          data: [],
          silent: true,
          markArea: {
            itemStyle: {
              color: dark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.10)",
            },
            data: [[{ xAxis: forecastStart }, { xAxis: lastDate }]],
          },
          tooltip: { show: false },
        });
      }

      const today = iso(new Date());
      series.push({
        name: "Today",
        type: "line",
        data: [],
        silent: true,
        markLine: {
          symbol: "none",
          label: {
            show: true,
            formatter: "Today",
            color: themeText,
            backgroundColor: "transparent",
          },
          lineStyle: { type: "dotted", color: dark ? "#9ca3af" : "#6b7280" },
          data: [{ xAxis: today }],
        },
        tooltip: { show: false },
      });

      for (const s of STREAMS) {
        if (!streams.includes(s)) continue;

        const color = COLORS[s];
        const ptsA = (preparedActual[s] || []).map(toPair).filter(Boolean);

        const fEntry = data?.forecast?.[s];
        const { mid: fMid, p10, p90, hasBand } = extractForecast(fEntry);

        series.push({
          name: `${s} Actual`,
          type: "scatter",
          symbolSize: 5,
          data: ptsA,
          itemStyle: { color },
          emphasis: { itemStyle: { borderColor: "#000", borderWidth: 1 } },
          animation: false,
          z: 3,
        });

        if (showMA) {
          series.push({
            name: `${s} ${maWindow}D Avg`,
            type: "line",
            showSymbol: false,
            smooth: true,
            lineStyle: { width: 2, opacity: 0.95 },
            itemStyle: { color },
            data: movingAverage(preparedActual[s] || [], maWindow),
            z: 2,
          });
        }

        if (showBands && hasBand && p10.length && p90.length) {
          const { a: lo, b: hi } = datesIntersection(p10, p90);
          const diff = lo.map(([d, v], i) => [d, hi[i][1] - v]);
          const bandStack = `${s}-band`;

          series.push({
            name: `${s} p10`,
            type: "line",
            showSymbol: false,
            lineStyle: { opacity: 0 },
            itemStyle: { color },
            data: lo,
            stack: bandStack,
            tooltip: { show: false },
            z: 1,
          });

          const bandColor = hexToRgba(color, dark ? 0.18 : 0.16);
          series.push({
            name: `${s} p10–p90`,
            type: "line",
            showSymbol: false,
            lineStyle: { opacity: 0 },
            areaStyle: { opacity: 1, color: bandColor },
            itemStyle: { color },
            data: diff,
            stack: bandStack,
            z: 1,
          });
        }

        if (fMid.length) {
          series.push({
            name: `${s} Forecast (p50)`,
            type: "line",
            showSymbol: false,
            data: fMid,
            lineStyle: { type: "dashed", width: 2 },
            itemStyle: { color },
            z: 2,
          });
        }

        if (showOutliers) {
          const outs = outlierDates(preparedActual[s] || []);
          if (outs.size) {
            const outPts = (preparedActual[s] || [])
              .filter((p) => outs.has(p.date) && definedNum(p?.q))
              .map((p) => [p.date, p.q]);
            series.push({
              name: `${s} Outliers`,
              type: "scatter",
              symbol: "triangle",
              symbolSize: 9,
              data: outPts,
              itemStyle: { color: dark ? "#fbbf24" : "#b45309" },
              z: 4,
            });
          }
        }

        const apiAnoms = (data?.anomalies || {})[s] || [];
        if (apiAnoms.length) {
          const anPts = apiAnoms
            .filter((a) => definedNum(a?.q))
            .map((a) => [a.date, a.q, a.resid]);
          series.push({
            name: `${s} Anomalies`,
            type: "scatter",
            symbol: "pin",
            symbolSize: (val) => {
              const resid = Math.abs(val?.[2] ?? 0);
              return Math.max(10, Math.min(20, 10 + resid * 10));
            },
            data: anPts,
            itemStyle: { color: dark ? "#f87171" : "#dc2626" },
            z: 5,
          });
        }
      }
    }

    return {
      backgroundColor: dark ? "#0b1220" : "#ffffff",
      textStyle: { color: dark ? "#e5e7eb" : "#111827" },
      grid: { left: 64, right: 24, top: 64, bottom: 64, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: dark
          ? "rgba(17,24,39,0.92)"
          : "rgba(255,255,255,0.96)",
        textStyle: { color: dark ? "#e5e7eb" : "#111827" },
        confine: true,
        formatter: (params) => {
          if (!params?.length) return "";
          const d = params[0].axisValueLabel;
          const lines = [
            `<div style="font-weight:700;margin-bottom:4px">${d}</div>`,
          ];
          for (const p of params) {
            const stream = STREAMS.find((s) => p.seriesName?.startsWith(s));
            const unit = stream ? unitsByStream[stream] || "" : "";
            const bullet = `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background:${p.color}"></span>`;
            const val = p.data?.[1] ?? p.value ?? null;
            lines.push(
              `${bullet}${p.seriesName}: <b>${withUnits(Number(val), unit)}</b>`,
            );
          }
          return lines.join("<br/>");
        },
      },
      legend: {
        top: 8,
        type: "scroll",
        textStyle: { color: dark ? "#e5e7eb" : "#111827" },
        pageIconColor: dark ? "#e5e7eb" : "#111827",
      },
      xAxis: {
        type: "time",
        min: domain.min || undefined,
        max: domain.max || undefined,
        axisLine: { lineStyle: { color: dark ? "#1f2937" : "#e5e7eb" } },
        axisLabel: { color: dark ? "#e5e7eb" : "#111827" },
        splitLine: {
          lineStyle: { color: dark ? "#1f2937" : "#e5e7eb", opacity: 0.3 },
        },
      },
      yAxis: {
        type: logScale ? "log" : "value",
        min: minForYAxis(),
        minorTick: { show: logScale },
        minorSplitLine: { show: logScale },
        axisLabel: {
          color: dark ? "#e5e7eb" : "#111827",
          formatter: (v) => (v >= 1000 ? `${fmt.format(v)}` : fmt.format(v)),
        },
        splitLine: {
          lineStyle: { color: dark ? "#1f2937" : "#e5e7eb", opacity: 0.3 },
        },
      },
      dataZoom: [
        { type: "inside", throttle: 50, start: 0, end: 100 },
        { type: "slider", height: 22, bottom: 24, start: 0, end: 100 },
      ],
      series,
      animation: false,
    };
  }, [
    data,
    logScale,
    streams,
    showMA,
    maWindow,
    showOutliers,
    showBands,
    dark,
    preparedActual,
    domain,
    unitsByStream,
  ]);

  const econChartOption = useMemo(() => {
    const res = econBundle?.selectedResult;
    if (!res?.rows?.length) return null;

    const themeText = dark ? "#e5e7eb" : "#111827";
    const rows = res.rows;

    const cf = rows.map((r) => [
      r.date,
      definedNum(r.cashflow) ? r.cashflow : 0,
    ]);
    const cum = rows.map((r) => [
      r.date,
      definedNum(r.cumCashflow) ? r.cumCashflow : 0,
    ]);
    const pv = rows.map((r) => [r.date, definedNum(r.pv) ? r.pv : 0]);

    return {
      backgroundColor: dark ? "#0b1220" : "#ffffff",
      textStyle: { color: themeText },
      grid: { left: 64, right: 24, top: 48, bottom: 40, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        confine: true,
        backgroundColor: dark
          ? "rgba(17,24,39,0.92)"
          : "rgba(255,255,255,0.96)",
        textStyle: { color: themeText },
        formatter: (params) => {
          if (!params?.length) return "";
          const d = params[0].axisValueLabel;
          const lines = [
            `<div style="font-weight:700;margin-bottom:4px">${d}</div>`,
          ];
          for (const p of params) {
            const val = Number(p.data?.[1] ?? 0);
            lines.push(`${p.seriesName}: <b>${money(val, 0)}</b>`);
          }
          return lines.join("<br/>");
        },
      },
      legend: {
        top: 8,
        textStyle: { color: themeText },
      },
      xAxis: {
        type: "time",
        axisLine: { lineStyle: { color: dark ? "#1f2937" : "#e5e7eb" } },
        axisLabel: { color: themeText },
        splitLine: {
          lineStyle: { color: dark ? "#1f2937" : "#e5e7eb", opacity: 0.25 },
        },
      },
      yAxis: [
        {
          type: "value",
          axisLabel: { color: themeText },
          splitLine: {
            lineStyle: { color: dark ? "#1f2937" : "#e5e7eb", opacity: 0.25 },
          },
        },
      ],
      series: [
        {
          name: "Daily Cashflow",
          type: "bar",
          data: cf,
        },
        {
          name: "Cumulative Cashflow",
          type: "line",
          showSymbol: false,
          data: cum,
        },
        {
          name: "Discounted CF (PV stream)",
          type: "line",
          showSymbol: false,
          lineStyle: { type: "dashed" },
          data: pv,
        },
      ],
      animation: false,
    };
  }, [econBundle, dark]);

  function handleExportPNG() {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst) return;
    const url = inst.getDataURL({
      type: "png",
      pixelRatio: 3,
      backgroundColor: dark ? "#0b1220" : "#ffffff",
    });
    const a = document.createElement("a");
    a.href = url;
    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(/\s+/g, "_")}_forecast_${lease}_${iso(new Date())}.png`;
    a.click();
  }

  function handleExportCSV() {
    if (!data) return;
    const blob = new Blob([toCSV(data)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(/\s+/g, "_")}_forecast_${lease}_${iso(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleExportJSON() {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(/\s+/g, "_")}_forecast_${lease}_${iso(new Date())}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleExportEconCSV() {
    const res = econBundle?.selectedResult;
    if (!res) return;
    const blob = new Blob([toEconCSV(res)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(
        /\s+/g,
        "_",
      )}_econ_${econBundle?.selectedKey || "Base"}_${lease}_${iso(
      new Date(),
    )}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggleStream(s) {
    setStreams((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  function resetZoom() {
    const inst = chartRef.current?.getEchartsInstance?.();
    inst?.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
  }

  async function copyLink() {
    try {
      // Copy the *staged overlay API URL* (short, leaseid-based), not the UI URL.
      const apiUrl = buildOverlayUrl({
        lease,
        horizon,
        streams: streams.join(","),
        ...(selectedTags.length ? { tags: selectedTags.join(",") } : {}),
      });
      await navigator.clipboard.writeText(apiUrl);
    } catch {}
  }

  function onTagsChange(e) {
    const values = Array.from(e.target.selectedOptions)
      .map((o) => String(o.value || "").trim())
      .filter(Boolean);

    // option.value MUST be TagID
    setSelectedTags(values);
  }

  const subtitle = `Lease: ${
    !lease
      ? "—"
      : lease === "~ALL~"
        ? "~ALL~"
        : leaseNameById.get(lease) || lease
  }${
    selectedTags.length
      ? ` • Tags: ${selectedTags
          .map((id) => tagDescById.get(id) || id)
          .join(", ")}`
      : ""
  }`;

  // ADMIN GATE (render-time, AFTER hooks)
  if (!init.isAdmin) return null;

  const econSelected = econBundle?.selectedResult || null;
  const econDecisionColor =
    econSelected?.decision === "Shut-in candidate"
      ? "#ef4444"
      : econSelected?.decision === "Monitor"
        ? "#f59e0b"
        : undefined;

  const effectiveWI = computeEffectiveWI(econ);
  const derivedNRI = (1 - clamp01(econ.royalty)) * effectiveWI; // DO NOT MISS A LINE

  // ---------- Econ tooltips (UI) ----------
  const ECON_TIPS = {
    // Section-level (headers)
    sections: {
      decision: `Decision logic (what this panel means)
• We build a DAILY cashflow series using actual + forecast(p50) volumes.
• Daily CF = OwnerRev − Opex − CapexOwner.
• "First negative CF" = first day CF < 0 in the econ window.
• "Economic limit" = earliest trigger date from:
  - Rolling-average rule (avg CF over N days < 0), OR
  - Consecutive-negative rule (N days CF < 0).
• Labels:
  - Operate: no near-term triggers.
  - Monitor: first negative CF soon, but no econ-limit trigger yet.
  - Shut-in candidate: econ-limit trigger hits within the window.`,
      kpis: `KPIs (computed over the econ window)
• PV: sum of discounted daily cashflows (discounted from window start using the annual discount rate).
  - If discountRateAnnual = 0.10, this is effectively PV10 over the window.
• Cumulative CF: sum of UNDISCOUNTED daily cashflows over the window.
• Netback ($/boe): totalCash / totalBOE (owner-level net cash per BOE).
  - Includes royalties, taxes, LOE, and capex timing.
• LOE ($/boe): totalOpex / totalBOE (owner-level operating cost per BOE).
• Payout date: first day cumulative CF ≥ 0 (most meaningful when you include upfront capex).`,
      breakevens: `Breakevens & sensitivities (what these mean)
• These solve for a single input value while holding everything else constant.
• "Breakeven Oil for PV=0":
  - Finds the oil price where PV over the window equals zero.
• "Breakeven Oil for Min CF ≥ 0":
  - Finds the oil price where the WORST daily CF in the window is ≥ 0 (very conservative).
• "Max Fixed LOE for PV ≥ 0":
  - Finds the highest monthly fixed LOE that still yields PV ≥ 0.`,
      prices: `Prices & volume adjustments
• Oil/Gas price are the benchmark prices before deductions.
• Net price used in revenue:
  - Oil net = OilPrice − OilDiff − OilMkt − OilTrans
  - Gas net = GasPrice − GasDiff − GasMkt − GasTrans
• Gas shrink reduces sellable gas: salesGas = producedGas × (1 − shrink).
• Downtime reduces produced volumes: uptime = 1 − downtimePct; volumes × uptime.`,
      marketing: `Marketing / transport
• Per-unit deductions from price (gathering, treating, marketing, fuel, trucking, pipeline tariffs, etc.).
• These reduce realized price directly (not volumes).`,
      ownership: `Ownership & taxes
• Royalty is taken off gross revenue first (before WI).
• WI (working interest) is the fraction of post-royalty revenue AND costs you own.
• Optional NRI override:
  - If set (>0), treat it as NRI and derive WI ≈ NRI / (1 − royalty).
• Severance taxes are applied on gross stream revenue (oil & gas separately).
• Ad valorem is modeled as a % of gross revenue.`,
      opex: `Opex (LOE) model
• Fixed LOE/month: daily fixed cost bucket (labor, routine ops, etc.).
• Var LOE/boe: per-BOE lifting cost.
• Water disposal: $/bbl water (often dominant late-life).
• Base maint/month: baseline maintenance.
• Low-rate extra maint: adds extra daily maintenance when BOE/day falls below a threshold.
• Chemicals/electricity: per-BOE variable consumables.
• Overhead/month: admin/field overhead.
• Owner Opex = (all opex components) × WI.`,
      capex: `Capex / discount / rules
• Capex upfront/date: applied as a one-day cost on the capex date (owner share × WI).
• Abandonment cost/date: applied as a one-day cost on the abandonment date (owner share × WI).
• Discount rate annual: PV discounts daily CF from the window start:
  PV = Σ CF_day × 1/(1+r)^(days/365)
• Econ window days: forward-looking horizon from Thru used for econ evaluation.
• Rolling avg days: economic limit when rolling avg CF < 0.
• Consecutive neg days: economic limit when CF < 0 for N consecutive days.
• Economic limit date is the EARLIEST of those triggers.`,
    },

    // Summary row labels
    summary: {
      firstNegDate: `First day in the econ window where daily owner-level cashflow < 0.
Owner CF already includes: royalty, taxes, WI share of LOE, and capex timing.`,
      econLimitDate: `Economic limit date = earliest trigger date among:
• Rolling-average cashflow < 0 (over N days), OR
• N consecutive negative cashflow days.`,
      rollingNegDate: `Rolling-average trigger:
We compute avg(daily CF) over the last N days.
If that average drops below 0, the well is considered uneconomic by this rule.`,
      consecutiveNegDate: `Consecutive-negative trigger:
If daily CF is negative for N straight days, we flag economic limit.`,
      pv: `PV = sum of discounted daily cashflows in the econ window.
Discounting is from the window start date using the annual discount rate.`,
      totalCash: `Cumulative CF = sum of undiscounted daily cashflows over the window.`,
      netback: `Netback ($/boe) = totalCash / totalBOE over the window.
This is an owner-level net cash metric (after royalties/taxes/LOE/capex).`,
      loePerBoe: `LOE ($/boe) = totalOpex / totalBOE over the window.
Opex includes fixed+variable LOE, water disposal, base/low-rate maint, chemicals, electricity, overhead.`,
      payoutDate: `Payout date = first day cumulative cashflow (starting at window start) becomes ≥ 0.
Most meaningful when you include upfront capex or large early costs.`,
    },

    // Breakeven row labels
    breakevens: {
      beOilPV0: `Oil price that makes PV over the selected econ window equal to 0.
Solved via bisection while keeping all other assumptions fixed.`,
      beOilMinCF0: `Oil price that makes the MINIMUM daily cashflow in the window ≥ 0.
This is conservative because it requires every day to be non-negative.`,
      maxFixedLoePV0: `Maximum monthly fixed LOE that still yields PV ≥ 0 over the window.
Useful for “how much fixed overhead can this tolerate?” sensitivity.`,
    },

    // Assumptions field labels
    assumptions: {
      oilPrice: `Benchmark oil price used for revenue before deductions (diff/marketing/transport).
Realized (net) oil price = OilPrice − OilDiff − OilMkt − OilTrans.`,
      gasPrice: `Benchmark gas price used for revenue before deductions (diff/marketing/transport).
Realized (net) gas price = GasPrice − GasDiff − GasMkt − GasTrans.`,
      oilDiff: `Oil differential/basis penalty ($/bbl).
Subtracts from benchmark to represent quality, location, gravity, basis, etc.`,
      gasDiff: `Gas differential/basis penalty ($/mcf).
Subtracts from benchmark to represent basis, BTU, gathering penalties, etc.`,
      gasShrink: `Fraction of produced gas that is NOT sold (fuel/plant shrink/measurement).
Sales gas = produced gas × (1 − shrink).`,
      downtimePct: `Fraction of time the well is down.
We apply uptime = 1 − downtimePct and multiply volumes by uptime.`,

      oilMarketingPerBbl: `Per-bbl marketing/treating/gathering fees for oil.
Directly reduces realized oil price.`,
      gasMarketingPerMcf: `Per-mcf marketing/treating/gathering fees for gas.
Directly reduces realized gas price.`,
      oilTransportPerBbl: `Per-bbl transport cost for oil (trucking/pipeline tariffs).
Directly reduces realized oil price.`,
      gasTransportPerMcf: `Per-mcf transport cost for gas (pipeline tariffs, compression fees, etc.).
Directly reduces realized gas price.`,

      royalty: `Royalty rate (0–1). Paid on gross revenue BEFORE WI.
Owner revenue is computed after royalty and taxes, then multiplied by WI.`,
      workingInterest: `Working interest (0–1). Your share of:
• post-royalty revenue, and
• operating/capex costs.`,
      nriOverride: `Optional NRI (0–1). If set (>0), we treat it as NRI and derive WI:
WI ≈ NRI / (1 − royalty). Useful when you know NRI but not WI explicitly.`,
      oilSevTax: `Oil severance tax rate (0–1) applied to gross oil revenue.`,
      gasSevTax: `Gas severance tax rate (0–1) applied to gross gas revenue.`,
      adValoremTax: `Ad valorem tax rate (0–1) applied to total gross revenue.`,

      fixedLoePerMonth: `Fixed operating cost per month (labor, routine ops, fixed contracts).
Converted to a daily cost bucket: fixed/30.`,
      varLoePerBoe: `Variable operating cost per BOE (lifting costs that scale with rate).`,
      waterDisposalPerBbl: `Cost per bbl of produced water (SWD fees, hauling, etc.).`,
      chemicalsPerBoe: `Chemicals/consumables cost per BOE.`,
      electricityPerBoe: `Electric power cost per BOE (pumping, compression, etc.).`,
      overheadPerMonth: `Monthly overhead allocated to the well (admin/field overhead).
Converted to daily: overhead/30.`,
      baseMaintPerMonth: `Baseline maintenance per month (routine repairs).
Converted to daily: baseMaint/30.`,
      lowRateBoePerDayThreshold: `If BOE/day is below this threshold, we add the low-rate extra maintenance daily.`,
      lowRateExtraMaintPerMonth: `Extra maintenance per month applied only when BOE/day < threshold.
Converted to daily: lowRateExtra/30.`,

      capexUpfront: `One-time capital cost applied on the capex date (e.g., workover, recompletion).
Owner share = capex × WI.`,
      capexDate: `Date to apply the upfront capex (YYYY-MM-DD).
If blank, defaults to the econ window start date.`,
      abandonmentCost: `One-time abandonment / P&A cost applied on the abandonment date.
Owner share = abandonment × WI.`,
      abandonmentDate: `Date to apply abandonment cost (YYYY-MM-DD).
If blank, defaults to the last day in the econ window.`,
      discountRateAnnual: `Annual discount rate used for PV.
PV discounts each day’s cashflow from window start by (1+r)^(days/365).`,
      unprofitableSoonDays: `How far forward (days) we evaluate econ starting from Thru.
This is the horizon used for PV, decision logic, and triggers.`,
      econLimitRollingDays: `Window size N for rolling-average economic limit rule.
If avg(CF) over last N days < 0 → rolling trigger date.`,
      econLimitConsecutiveNegDays: `N for consecutive-negative economic limit rule.
If CF < 0 for N consecutive days → consecutive trigger date.`,
    },
  };

  function InfoTip({ text, align = "left" }) {
    if (!text) return null;
    return (
      <span
        className={`tipWrap ${align === "right" ? "right" : ""}`}
        tabIndex={0}
        role="button"
        aria-label="Info"
      >
        <span className="tipIcon" aria-hidden="true">
          i
        </span>
        <span className="tipBubble" role="tooltip">
          {text}
        </span>
      </span>
    );
  }

  return (
    <div className="forecast-root" data-theme={dark ? "dark" : "light"}>
      <style>{`
        :root {
          --bg: #ffffff; --panel: #f8fafc; --text: #111827; --muted: #6b7280;
          --ring: #3b82f6; --border: #e5e7eb; --chip: #eef2ff;
          --brand: #111827;
          --shadow: 0 10px 30px rgba(0,0,0,0.08);
        }
        [data-theme="dark"] {
          --bg: #0b1220; --panel: #0f172a; --text: #e5e7eb; --muted: #9ca3af;
          --ring: #60a5fa; --border: #1f2937; --chip: #111827;
          --brand: #e5e7eb;
          --shadow: 0 12px 40px rgba(0,0,0,0.35);
        }
        .forecast-root { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; background: var(--bg); color: var(--text); min-height: 100vh; }
        .topbar { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid var(--border); position: sticky; top:0; background: var(--bg); z-index: 40;}
        .brand { display:flex; align-items:center; gap:10px; }
        .logo { width:20px; height:20px; border-radius:5px; background: linear-gradient(135deg, #2563eb 0%, #10b981 100%); }
        .title { font-weight: 800; font-size: 14px; color: var(--brand); }
        .subtitle { color: var(--muted); font-size: 12px; }
        .layout { display:grid; grid-template-columns: 340px 1fr; gap: 0; }
        @media (max-width: 980px){ .layout { grid-template-columns: 1fr; } }
        .panel { background: var(--panel); border-right: 1px solid var(--border); padding: 12px; }
        .panel h3 { margin: 8px 0 6px; font-size: 12px; text-transform: uppercase; color: var(--muted); letter-spacing:.08em; }
        .row { display:flex; gap:8px; align-items:center; margin: 8px 0; flex-wrap: wrap; }
        .row label { font-size: 12px; color: var(--muted); }
        .row input[type="date"], .row input[type="number"], .row input[type="text"], .row select {
          background: var(--bg); border:1px solid var(--border); color: var(--text); border-radius:8px; padding:6px 8px;
        }
        .row select[multiple] { min-height: 96px; }
        .chips { display:flex; gap:8px; flex-wrap:wrap; }
        .chip { border:1px solid var(--border); background: var(--chip); padding:6px 10px; border-radius:999px; cursor:pointer; font-weight:600; font-size:12px; }
        .chip.on { outline: 2px solid var(--ring); }
        .actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        .btn { background: var(--bg); border:1px solid var(--border); border-radius:10px; padding:8px 12px; cursor:pointer; display:inline-flex; gap:8px; align-items:center; }
        .btn.primary { background:#3b82f6; border-color:#2563eb; color:white; }
        .btn.warn { background: rgba(245,158,11,0.15); border-color: rgba(245,158,11,0.45); }
        .btn.danger { background: rgba(239,68,68,0.14); border-color: rgba(239,68,68,0.45); }
        .btn:disabled { opacity:.6; cursor:default; }
        .pill { font-size: 11px; padding: 2px 8px; border-radius: 999px; border:1px solid var(--border); color: var(--muted); }
        .pill.good { border-color: rgba(16,185,129,0.45); color: ${dark ? "#34d399" : "#059669"}; }
        .pill.warn { border-color: rgba(245,158,11,0.55); color: ${dark ? "#fbbf24" : "#b45309"}; }
        .pill.bad { border-color: rgba(239,68,68,0.55); color: ${dark ? "#f87171" : "#b91c1c"}; }
        .content { padding: 8px 12px; }
        .cards { display:grid; grid-template-columns: repeat(3, minmax(220px, 1fr)); gap: 8px; margin: 8px 0 12px; }
        @media (max-width: 980px){ .cards{ grid-template-columns: 1fr; } }
        .card { background: var(--panel); border:1px solid var(--border); border-radius: 14px; padding: 12px; box-shadow: none; }
        .card h4 { margin: 0 0 4px; font-size: 12px; text-transform: uppercase; letter-spacing:.08em; color: var(--muted); }
        .card .val { font-weight: 800; font-size: 18px; }
        .card .sub { color: var(--muted); font-size: 12px; }
        .footnote { color: var(--muted); font-size: 12px; margin-top: 8px; }
        .err { color: #ef4444; }
        .skeleton { height: 520px; border:1px dashed var(--border); border-radius: 12px; display:flex; align-items:center; justify-content:center; color: var(--muted); }
        .econBox { background: var(--bg); border:1px solid var(--border); border-radius: 12px; padding: 10px; margin-top: 10px; }
        .econGrid { display:grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .econKpi { font-size: 12px; color: var(--muted); }
        .econKpi b { color: var(--text); font-size: 13px; }
        .kpiStrip { display:flex; gap:10px; flex-wrap:wrap; margin: 6px 0 10px; }
        .kpiChip { background: var(--panel); border:1px solid var(--border); border-radius: 999px; padding: 6px 10px; font-size: 12px; color: var(--muted); display:flex; gap:6px; align-items:center; }
        .kpiChip b { color: var(--text); }
        .divider { height: 1px; background: var(--border); margin: 10px 0; opacity: 0.8; }

        /* Snapshot stamp (side panel) */
        .stampBox {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 10px 12px;
          margin: 8px 0 10px;
          box-shadow: none;
        }
        .stampHeader {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 10px;
        }
        .stampLabel {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .08em;
          color: var(--muted);
          font-weight: 800;
          line-height: 1.2;
        }
        .stampValue {
          margin-top: 4px;
          font-size: 16px;
          font-weight: 950;
          color: var(--text);
          line-height: 1.15;
          font-variant-numeric: tabular-nums;
          word-break: break-word;
        }
        .stampSubRow {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 8px;
        }

        /* Tooltips */
        .h5Row { display:flex; align-items:center; gap:8px; }
        .kpiLabel { display:inline-flex; align-items:center; gap:6px; }
        .econKpiLabel { display:flex; align-items:center; gap:6px; }
        .tipWrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          cursor: help;
          outline: none;
        }
        .tipIcon {
          width: 16px;
          height: 16px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 900;
          color: var(--text);
          border: 1px solid var(--border);
          background: var(--bg);
          opacity: 0.85;
          user-select: none;
          line-height: 1;
        }
        .tipWrap:hover .tipIcon { opacity: 1; }
        .tipWrap:focus-visible .tipIcon {
          outline: 2px solid var(--ring);
          outline-offset: 2px;
          opacity: 1;
        }
        .tipBubble {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          width: min(520px, 86vw);
          max-height: min(60vh, 360px);
          overflow: auto;
          overscroll-behavior: contain;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 14px;
          box-shadow: var(--shadow);
          padding: 10px 12px;
          font-size: 12px;
          line-height: 1.35;
          color: var(--text);
          white-space: pre-wrap;
          word-break: break-word;
          z-index: 9999;
          opacity: 0;
          transform: translateY(4px);
          pointer-events: none;
          transition: opacity .14s ease, transform .14s ease;
        }
        .tipWrap.right .tipBubble { left: auto; right: 0; }
        .tipWrap:hover .tipBubble,
        .tipWrap:focus .tipBubble,
        .tipWrap:focus-within .tipBubble {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        .tipBubble::before {
          content: "";
          position: absolute;
          top: -6px;
          left: 12px;
          width: 10px;
          height: 10px;
          background: var(--bg);
          border-left: 1px solid var(--border);
          border-top: 1px solid var(--border);
          transform: rotate(45deg);
        }
        .tipWrap.right .tipBubble::before { left: auto; right: 14px; }

        @media (prefers-reduced-motion: reduce){
          .tipBubble { transition: none; }
        }

        /* Modal */
        .modalOverlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          padding: 14px;
          z-index: 80;
          overflow: auto;
          -webkit-overflow-scrolling: touch;
        }
        .modalCard {
          width: min(980px, 100%);
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 16px;
          box-shadow: var(--shadow);
          display:flex; flex-direction: column;
          margin: 0 auto;
          overflow: visible; /* IMPORTANT: don't clip tooltips */
        }
        .modalHeader {
          display:flex; align-items:center; justify-content:space-between;
          padding: 12px 14px;
          border-bottom: 1px solid var(--border);
          background: var(--bg);
          position: sticky;
          top: 0;
          z-index: 2;
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }
        .modalTitle { font-weight: 900; font-size: 14px; color: var(--brand); }
        .modalSub { font-size: 12px; color: var(--muted); margin-top: 2px; }
        .modalBody { padding: 12px 14px; overflow: visible; } /* IMPORTANT: don't clip tooltips */
        .modalFooter {
          padding: 10px 14px;
          border-top: 1px solid var(--border);
          background: var(--bg);
          display:flex; justify-content:space-between; gap: 10px; flex-wrap:wrap;
          position: sticky;
          bottom: 0;
          z-index: 2;
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }
        .tabs { display:flex; gap:8px; flex-wrap: wrap; margin-bottom: 10px; }
        .tab { padding: 6px 10px; border-radius: 999px; border:1px solid var(--border); background: var(--bg); cursor: pointer; font-size: 12px; }
        .tab.on { outline: 2px solid var(--ring); font-weight: 800; }
        .grid2 { display:grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        @media (max-width: 900px){ .grid2{ grid-template-columns: 1fr; } }
        .panelCard { background: var(--panel); border: 1px solid var(--border); border-radius: 14px; padding: 12px; }
        .panelCard h5 { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: var(--muted); }
        .kpiBig { font-weight: 900; font-size: 20px; }
        .kpiRow { display:flex; justify-content:space-between; gap: 10px; font-size: 12px; color: var(--muted); margin-top: 6px; }
        .kpiRow b { color: var(--text); }
        table.tbl { width: 100%; border-collapse: collapse; }
        .tbl th, .tbl td { border-bottom: 1px solid var(--border); padding: 8px 6px; font-size: 12px; text-align: left; vertical-align: top; }
        .tbl th { color: var(--muted); font-weight: 800; text-transform: uppercase; letter-spacing:.06em; font-size: 11px; }
        .tbl td.num { text-align: right; font-variant-numeric: tabular-nums; }
      `}</style>

      <div className="topbar">
        <div className="brand">
          <div className="logo" />
          <div>
            <div className="title">
              {init.companyLabel} • Production Forecasts
            </div>
            <div className="subtitle">{subtitle}</div>
          </div>
        </div>
        <div className="actions">
          {econSelected?.decision ? (
            <span
              className={`pill ${
                econSelected.decision === "Operate"
                  ? "good"
                  : econSelected.decision === "Monitor"
                    ? "warn"
                    : "bad"
              }`}
              title={econSelected.decisionDetail || ""}
            >
              Econ: {econSelected.decision}
            </span>
          ) : (
            <span className="pill">Econ: —</span>
          )}

          <button
            className={`btn ${
              econSelected?.decision === "Shut-in candidate"
                ? "danger"
                : econSelected?.decision === "Monitor"
                  ? "warn"
                  : ""
            }`}
            onClick={() => {
              setEconModalOpen(true);
              setEconTab("summary");
            }}
            disabled={!data}
            title="Open decision metrics & econ layer"
          >
            Econ & Decisions
            {econSelected?.econLimitDate ? (
              <span style={{ color: econDecisionColor, fontWeight: 900 }}>
                • {econSelected.econLimitDate}
              </span>
            ) : null}
          </button>

          <button className="btn" onClick={() => setDark((d) => !d)}>
            {dark ? "Light" : "Dark"} mode
          </button>
          <button className="btn" onClick={resetZoom}>
            Reset zoom
          </button>
          <button className="btn" onClick={copyLink}>
            Copy link
          </button>
          <button className="btn" onClick={handleExportCSV} disabled={!data}>
            Export CSV
          </button>
          <button className="btn" onClick={handleExportPNG} disabled={!data}>
            Export PNG
          </button>
          <button className="btn" onClick={handleExportJSON} disabled={!data}>
            Export JSON
          </button>
        </div>
      </div>

      <Modal
        open={econModalOpen}
        title={`Econ & Decision Metrics • ${econBundle?.selectedName || "Base"} scenario`}
        onClose={() => setEconModalOpen(false)}
        footer={
          <>
            <div className="actions">
              <span
                className="pill"
                title="Derived from royalty + WI (or NRI override)"
              >
                NRI: {pct.format(clamp01(derivedNRI))}
              </span>
              <span
                className="pill"
                title="Working interest used in calculations"
              >
                WI: {pct.format(clamp01(effectiveWI))}
              </span>
              <span className="pill" title="Discount rate used for PV">
                Disc: {pct.format(clamp01(econ.discountRateAnnual))}
              </span>
            </div>
            <div className="actions">
              <button
                className="btn"
                onClick={handleExportEconCSV}
                disabled={!econSelected}
              >
                Export Econ CSV
              </button>
              <button
                className="btn primary"
                onClick={() => setEconModalOpen(false)}
              >
                Done
              </button>
            </div>
          </>
        }
      >
        <div className="tabs">
          <button
            className={`tab ${econTab === "summary" ? "on" : ""}`}
            onClick={() => setEconTab("summary")}
          >
            Summary
          </button>
          <button
            className={`tab ${econTab === "assumptions" ? "on" : ""}`}
            onClick={() => setEconTab("assumptions")}
          >
            Assumptions
          </button>
          <button
            className={`tab ${econTab === "table" ? "on" : ""}`}
            onClick={() => setEconTab("table")}
          >
            Table
          </button>

          <div style={{ flex: 1 }} />

          <label style={{ fontSize: 12, color: "var(--muted)" }}>
            Scenario
          </label>
          <select
            value={econScenarioKey}
            onChange={(e) => setEconScenarioKey(e.target.value)}
            style={{
              background: "var(--bg)",
              border: "1px solid var(--border)",
              color: "var(--text)",
              borderRadius: 10,
              padding: "6px 8px",
            }}
          >
            {ECON_SCENARIOS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {econTab === "summary" && (
          <>
            <div className="grid2">
              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Decision
                    <InfoTip text={ECON_TIPS.sections.decision} />
                  </span>
                </h5>
                <div
                  className="kpiBig"
                  style={{
                    color:
                      econSelected?.decision === "Shut-in candidate"
                        ? "#ef4444"
                        : econSelected?.decision === "Monitor"
                          ? "#f59e0b"
                          : undefined,
                  }}
                >
                  {econSelected?.decision || "—"}
                </div>
                <div className="kpiRow">
                  <span>
                    {econSelected?.decisionDetail ||
                      "Run overlay to compute econ."}
                  </span>
                </div>

                <div className="divider" />

                <div className="kpiRow">
                  <span className="kpiLabel">
                    First negative CF
                    <InfoTip text={ECON_TIPS.summary.firstNegDate} />
                  </span>
                  <b>{econSelected?.firstNegDate || "—"}</b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Economic limit date
                    <InfoTip text={ECON_TIPS.summary.econLimitDate} />
                  </span>
                  <b>{econSelected?.econLimitDate || "—"}</b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Rolling avg trigger
                    <InfoTip text={ECON_TIPS.summary.rollingNegDate} />
                  </span>
                  <b>{econSelected?.rollingNegDate || "—"}</b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Consecutive neg trigger
                    <InfoTip text={ECON_TIPS.summary.consecutiveNegDate} />
                  </span>
                  <b>{econSelected?.consecutiveNegDate || "—"}</b>
                </div>
              </div>

              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    KPIs
                    <InfoTip text={ECON_TIPS.sections.kpis} />
                  </span>
                </h5>
                <div className="kpiBig">{money(econSelected?.pv, 0)}</div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    PV (discounted stream)
                    <InfoTip text={ECON_TIPS.summary.pv} />
                  </span>
                  <b style={{ color: signColor(econSelected?.pv, dark) }}>
                    {econSelected?.pv != null ? "" : "—"}
                  </b>
                </div>

                <div className="divider" />

                <div className="kpiRow">
                  <span className="kpiLabel">
                    Cumulative CF (window)
                    <InfoTip text={ECON_TIPS.summary.totalCash} />
                  </span>
                  <b
                    style={{ color: signColor(econSelected?.totalCash, dark) }}
                  >
                    {money(econSelected?.totalCash, 0)}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Netback ($/boe)
                    <InfoTip text={ECON_TIPS.summary.netback} />
                  </span>
                  <b style={{ color: signColor(econSelected?.netback, dark) }}>
                    {definedNum(econSelected?.netback)
                      ? money(econSelected.netback, 2)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    LOE ($/boe)
                    <InfoTip text={ECON_TIPS.summary.loePerBoe} />
                  </span>
                  <b>
                    {definedNum(econSelected?.loePerBoe)
                      ? money(econSelected.loePerBoe, 2)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Payout date
                    <InfoTip text={ECON_TIPS.summary.payoutDate} />
                  </span>
                  <b>{econSelected?.payoutDate || "—"}</b>
                </div>
              </div>
            </div>

            <div className="grid2" style={{ marginTop: 10 }}>
              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Breakevens & Sensitivities
                    <InfoTip
                      text={ECON_TIPS.sections.breakevens}
                      align="right"
                    />
                  </span>
                </h5>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Breakeven Oil ($/bbl) for PV=0
                    <InfoTip
                      text={ECON_TIPS.breakevens.beOilPV0}
                      align="right"
                    />
                  </span>
                  <b>
                    {definedNum(econBundle?.breakevens?.beOilPV0)
                      ? fmt.format(econBundle.breakevens.beOilPV0)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Breakeven Oil ($/bbl) for Min CF ≥ 0
                    <InfoTip
                      text={ECON_TIPS.breakevens.beOilMinCF0}
                      align="right"
                    />
                  </span>
                  <b>
                    {definedNum(econBundle?.breakevens?.beOilMinCF0)
                      ? fmt.format(econBundle.breakevens.beOilMinCF0)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Max Fixed LOE ($/mo) for PV ≥ 0
                    <InfoTip
                      text={ECON_TIPS.breakevens.maxFixedLoePV0}
                      align="right"
                    />
                  </span>
                  <b>
                    {definedNum(econBundle?.breakevens?.maxFixedLoePV0)
                      ? fmt0.format(econBundle.breakevens.maxFixedLoePV0)
                      : "—"}
                  </b>
                </div>

                <div className="divider" />

                <div className="kpiRow">
                  <span className="kpiLabel">
                    Current Oil / Gas
                    <InfoTip text={ECON_TIPS.sections.prices} align="right" />
                  </span>
                  <b>
                    {fmt.format(
                      econBundle?.selectedEcon?.oilPrice || econ.oilPrice,
                    )}{" "}
                    /{" "}
                    {fmt.format(
                      econBundle?.selectedEcon?.gasPrice || econ.gasPrice,
                    )}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Window
                    <InfoTip
                      text={ECON_TIPS.assumptions.unprofitableSoonDays}
                      align="right"
                    />
                  </span>
                  <b>
                    {econSelected?.days || econ.unprofitableSoonDays}d from{" "}
                  </b>
                </div>
              </div>

              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Scenario compare
                    <InfoTip
                      text={`Scenario compare
• Each scenario applies multipliers to prices and (optionally) opex:
  - Base: 1.0×
  - Bear: lower prices + slightly higher opex
  - Bull: higher prices + slightly lower opex
• Decisions, PV, and Cum CF are recalculated for each.`}
                      align="right"
                    />
                  </span>
                </h5>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      <th className="num">PV</th>
                      <th className="num">Cum CF</th>
                      <th>Decision</th>
                      <th>Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ECON_SCENARIOS.map((sc) => {
                      const r = econBundle?.scenarios?.[sc.key]?.result;
                      const dec = r?.decision || "—";
                      const cls =
                        dec === "Operate"
                          ? "good"
                          : dec === "Monitor"
                            ? "warn"
                            : dec === "Shut-in candidate"
                              ? "bad"
                              : "";
                      return (
                        <tr key={sc.key}>
                          <td>
                            <b>{sc.name}</b>
                            {econScenarioKey === sc.key ? (
                              <span
                                style={{ marginLeft: 8 }}
                                className={`pill ${cls}`}
                              >
                                selected
                              </span>
                            ) : null}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r?.pv, dark) }}
                          >
                            {money(r?.pv, 0)}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r?.totalCash, dark) }}
                          >
                            {money(r?.totalCash, 0)}
                          </td>
                          <td>
                            <span className={`pill ${cls}`}>{dec}</span>
                          </td>
                          <td>{r?.econLimitDate || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="divider" />

                {econChartOption ? (
                  <ReactECharts
                    style={{
                      height: 240,
                      width: "100%",
                      border: `1px solid var(--border)`,
                      borderRadius: 12,
                    }}
                    option={econChartOption}
                    notMerge={true}
                    lazyUpdate={true}
                  />
                ) : (
                  <div className="subtitle">
                    Run overlay to view econ chart.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {econTab === "assumptions" && (
          <>
            <div className="grid2">
              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Prices
                    <InfoTip text={ECON_TIPS.sections.prices} />
                  </span>
                </h5>
                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil $/bbl
                      <InfoTip text={ECON_TIPS.assumptions.oilPrice} />
                    </span>
                    <input
                      type="number"
                      value={econ.oilPrice}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilPrice: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas $/mcf
                      <InfoTip text={ECON_TIPS.assumptions.gasPrice} />
                    </span>
                    <input
                      type="number"
                      value={econ.gasPrice}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasPrice: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil diff ($/bbl)
                      <InfoTip text={ECON_TIPS.assumptions.oilDiff} />
                    </span>
                    <input
                      type="number"
                      value={econ.oilDiff}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilDiff: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas diff ($/mcf)
                      <InfoTip text={ECON_TIPS.assumptions.gasDiff} />
                    </span>
                    <input
                      type="number"
                      value={econ.gasDiff}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasDiff: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas shrink
                      <InfoTip text={ECON_TIPS.assumptions.gasShrink} />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.gasShrink}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasShrink: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Downtime %
                      <InfoTip text={ECON_TIPS.assumptions.downtimePct} />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.downtimePct}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          downtimePct: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <h5>
                  <span className="h5Row">
                    Marketing / Transport
                    <InfoTip text={ECON_TIPS.sections.marketing} />
                  </span>
                </h5>
                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil mkt ($/bbl)
                      <InfoTip
                        text={ECON_TIPS.assumptions.oilMarketingPerBbl}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.oilMarketingPerBbl}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilMarketingPerBbl: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas mkt ($/mcf)
                      <InfoTip
                        text={ECON_TIPS.assumptions.gasMarketingPerMcf}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.gasMarketingPerMcf}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasMarketingPerMcf: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil trans ($/bbl)
                      <InfoTip
                        text={ECON_TIPS.assumptions.oilTransportPerBbl}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.oilTransportPerBbl}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilTransportPerBbl: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas trans ($/mcf)
                      <InfoTip
                        text={ECON_TIPS.assumptions.gasTransportPerMcf}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.gasTransportPerMcf}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasTransportPerMcf: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>
              </div>

              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Ownership & Taxes
                    <InfoTip
                      text={ECON_TIPS.sections.ownership}
                      align="right"
                    />
                  </span>
                </h5>
                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Royalty
                      <InfoTip
                        text={ECON_TIPS.assumptions.royalty}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.royalty}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          royalty: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Working Interest (WI)
                      <InfoTip
                        text={ECON_TIPS.assumptions.workingInterest}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.workingInterest}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          workingInterest: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      NRI override (optional)
                      <InfoTip
                        text={ECON_TIPS.assumptions.nriOverride}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.nriOverride}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          nriOverride: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <div className="econKpi" style={{ alignSelf: "end" }}>
                    Derived NRI: <b>{pct.format(clamp01(derivedNRI))}</b>
                  </div>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil sev tax
                      <InfoTip
                        text={ECON_TIPS.assumptions.oilSevTax}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.oilSevTax}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilSevTax: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas sev tax
                      <InfoTip
                        text={ECON_TIPS.assumptions.gasSevTax}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.gasSevTax}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasSevTax: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Ad valorem tax
                      <InfoTip
                        text={ECON_TIPS.assumptions.adValoremTax}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.adValoremTax}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          adValoremTax: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <h5>
                  <span className="h5Row">
                    Opex (LOE)
                    <InfoTip text={ECON_TIPS.sections.opex} align="right" />
                  </span>
                </h5>
                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Fixed LOE $/mo
                      <InfoTip
                        text={ECON_TIPS.assumptions.fixedLoePerMonth}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.fixedLoePerMonth}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          fixedLoePerMonth: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Var LOE $/boe
                      <InfoTip
                        text={ECON_TIPS.assumptions.varLoePerBoe}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.varLoePerBoe}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          varLoePerBoe: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Water disposal $/bbl
                      <InfoTip
                        text={ECON_TIPS.assumptions.waterDisposalPerBbl}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.waterDisposalPerBbl}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          waterDisposalPerBbl: parseFloat(
                            e.target.value || "0",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Chemicals $/boe
                      <InfoTip
                        text={ECON_TIPS.assumptions.chemicalsPerBoe}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.chemicalsPerBoe}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          chemicalsPerBoe: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Electricity $/boe
                      <InfoTip
                        text={ECON_TIPS.assumptions.electricityPerBoe}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.electricityPerBoe}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          electricityPerBoe: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Overhead $/mo
                      <InfoTip
                        text={ECON_TIPS.assumptions.overheadPerMonth}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.overheadPerMonth}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          overheadPerMonth: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Base maint $/mo
                      <InfoTip
                        text={ECON_TIPS.assumptions.baseMaintPerMonth}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.baseMaintPerMonth}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          baseMaintPerMonth: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Low-rate threshold (boe/d)
                      <InfoTip
                        text={ECON_TIPS.assumptions.lowRateBoePerDayThreshold}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.lowRateBoePerDayThreshold}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          lowRateBoePerDayThreshold: parseFloat(
                            e.target.value || "0",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Low-rate extra maint $/mo
                      <InfoTip
                        text={ECON_TIPS.assumptions.lowRateExtraMaintPerMonth}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.lowRateExtraMaintPerMonth}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          lowRateExtraMaintPerMonth: parseFloat(
                            e.target.value || "0",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <h5>
                  <span className="h5Row">
                    Capex / Discount / Rules
                    <InfoTip text={ECON_TIPS.sections.capex} align="right" />
                  </span>
                </h5>
                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Capex upfront ($)
                      <InfoTip
                        text={ECON_TIPS.assumptions.capexUpfront}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.capexUpfront}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          capexUpfront: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Capex date (YYYY-MM-DD)
                      <InfoTip
                        text={ECON_TIPS.assumptions.capexDate}
                        align="right"
                      />
                    </span>
                    <input
                      type="text"
                      value={econ.capexDate}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          capexDate: e.target.value,
                        }))
                      }
                      placeholder="defaults to window start"
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Abandonment cost ($)
                      <InfoTip
                        text={ECON_TIPS.assumptions.abandonmentCost}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.abandonmentCost}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          abandonmentCost: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Abandonment date
                      <InfoTip
                        text={ECON_TIPS.assumptions.abandonmentDate}
                        align="right"
                      />
                    </span>
                    <input
                      type="text"
                      value={econ.abandonmentDate}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          abandonmentDate: e.target.value,
                        }))
                      }
                      placeholder="defaults to last day"
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Discount rate (annual)
                      <InfoTip
                        text={ECON_TIPS.assumptions.discountRateAnnual}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.discountRateAnnual}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          discountRateAnnual: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Econ window (days)
                      <InfoTip
                        text={ECON_TIPS.assumptions.unprofitableSoonDays}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.unprofitableSoonDays}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          unprofitableSoonDays: parseFloat(
                            e.target.value || "60",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Rolling avg days
                      <InfoTip
                        text={ECON_TIPS.assumptions.econLimitRollingDays}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.econLimitRollingDays}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          econLimitRollingDays: parseFloat(
                            e.target.value || "14",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Consecutive neg days
                      <InfoTip
                        text={ECON_TIPS.assumptions.econLimitConsecutiveNegDays}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.econLimitConsecutiveNegDays}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          econLimitConsecutiveNegDays: parseFloat(
                            e.target.value || "14",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <div className="actions">
                  <button
                    className="btn"
                    onClick={() => {
                      setEcon({
                        // Prices & diffs
                        oilPrice: 70,
                        gasPrice: 3.0,
                        oilDiff: 3.0,
                        gasDiff: 0.3,
                        gasShrink: 0.05,

                        // Midstream / marketing / transport
                        oilMarketingPerBbl: 0.0,
                        gasMarketingPerMcf: 0.0,
                        oilTransportPerBbl: 0.0,
                        gasTransportPerMcf: 0.0,

                        // Ownership & taxes
                        royalty: 0.2,
                        workingInterest: 1.0,
                        nriOverride: 0.0,
                        oilSevTax: 0.046,
                        gasSevTax: 0.075,
                        adValoremTax: 0.0,

                        // Opex (LOE)
                        fixedLoePerMonth: 4500,
                        varLoePerBoe: 2.25,
                        waterDisposalPerBbl: 1.25,
                        chemicalsPerBoe: 0.0,
                        electricityPerBoe: 0.0,
                        overheadPerMonth: 0.0,
                        baseMaintPerMonth: 350,
                        lowRateBoePerDayThreshold: 8,
                        lowRateExtraMaintPerMonth: 900,

                        // Capex timing
                        capexUpfront: 0,
                        capexDate: "",
                        abandonmentCost: 0,
                        abandonmentDate: "",

                        // Uptime factor
                        downtimePct: 0.0,

                        // Discounting
                        discountRateAnnual: 0.1,

                        // Units scaling
                        oilUnitFactor: 1.0,
                        gasUnitFactor: 1.0,
                        waterUnitFactor: 1.0,

                        // Decision rules / window
                        unprofitableSoonDays: 60,
                        econLimitRollingDays: 14,
                        econLimitConsecutiveNegDays: 14,
                      });
                    }}
                  >
                    Reset defaults
                  </button>
                </div>
              </div>
            </div>

            <div className="footnote">
              Econ uses <b>daily</b> series (actual + forecast p50), applies
              WI/royalty/taxes/LOE, then flags economic limit via
              rolling-average or consecutive-negative rules.
            </div>
          </>
        )}

        {econTab === "table" && (
          <>
            <div className="panelCard">
              <h5>
                <span className="h5Row">
                  Daily Econ Table (selected scenario)
                  <InfoTip
                    text={`Daily econ table (columns)
• Owner Rev: (GrossRev − Royalty − Taxes) × WI
• Opex: LOE bucket × WI
• Capex: one-time costs on specified dates × WI
• Cashflow: OwnerRev − Opex − Capex
• Cum CF: running sum of cashflow
• PV: discounted cashflow using annual discount rate`}
                    align="right"
                  />
                </span>
              </h5>

              {!econSelected?.rows?.length ? (
                <div className="subtitle">
                  Run overlay to compute econ table.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="num">Oil</th>
                        <th className="num">Gas</th>
                        <th className="num">Water</th>
                        <th className="num">BOE</th>
                        <th className="num">Owner Rev</th>
                        <th className="num">Opex</th>
                        <th className="num">Capex</th>
                        <th className="num">Cashflow</th>
                        <th className="num">Cum CF</th>
                        <th className="num">PV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {econSelected.rows.map((r) => (
                        <tr key={r.date}>
                          <td>
                            <b>{r.date}</b>
                          </td>
                          <td className="num">
                            {definedNum(r.oil) ? fmt.format(r.oil) : "—"}
                          </td>
                          <td className="num">
                            {definedNum(r.gas) ? fmt.format(r.gas) : "—"}
                          </td>
                          <td className="num">
                            {definedNum(r.water) ? fmt.format(r.water) : "—"}
                          </td>
                          <td className="num">
                            {definedNum(r.boe) ? fmt.format(r.boe) : "—"}
                          </td>
                          <td className="num">{money(r.ownerRev, 0)}</td>
                          <td className="num">{money(r.opex, 0)}</td>
                          <td className="num">{money(r.capex, 0)}</td>
                          <td
                            className="num"
                            style={{ color: signColor(r.cashflow, dark) }}
                          >
                            {money(r.cashflow, 0)}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r.cumCashflow, dark) }}
                          >
                            {money(r.cumCashflow, 0)}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r.pv, dark) }}
                          >
                            {money(r.pv, 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      <div className="layout">
        <div className="panel">
          <h3>Lease</h3>
          <div className="row">
            <select
              value={lease}
              onChange={(e) => setLease(e.target.value)}
              style={{ width: "100%" }}
              disabled={ltLoading}
            >
              <option value="~ALL~">~ALL~</option>
              {leaseOptions.map((l) => (
                <option key={l.LeaseID} value={l.LeaseID}>
                  {l.LeaseName}
                </option>
              ))}
            </select>
          </div>

          <h3>Tags</h3>
          <div className="row">
            <select multiple value={selectedTags} onChange={onTagsChange}>
              {tagOptions.map((t) => (
                <option key={t.TagKey} value={t.TagKey}>
                  {t.TagDesc}
                </option>
              ))}
            </select>
          </div>
          {ltError ? <div className="footnote err">{ltError}</div> : null}
          {ltLoading ? (
            <div className="footnote">Loading leases/tags…</div>
          ) : null}

          <h3>Snapshot</h3>
          <div className="stampBox">
            <div className="stampHeader">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="stampLabel">Created at</div>
                <div className="stampValue">
                  {data?.meta?.created_at || "—"}
                </div>
              </div>
            </div>
            <div className="stampSubRow">
              <span className="pill" title="Last run at">
                Run: {runAt || "—"}
              </span>
              <span className="pill" title="Backend response time">
                Latency: {latencyMs != null ? `${latencyMs}ms` : "—"}
              </span>
            </div>
          </div>

          <h3>Forecast</h3>
          <div className="row">
            <label>
              Horizon (days)
              <div>
                <input
                  type="number"
                  min={7}
                  max={365}
                  value={horizon}
                  onChange={(e) =>
                    setHorizon(parseInt(e.target.value || "60", 10))
                  }
                  style={{ width: 120 }}
                />
              </div>
            </label>
          </div>

          <h3>Streams</h3>
          <div className="chips">
            {STREAMS.map((s) => (
              <div
                key={s}
                className={`chip ${streams.includes(s) ? "on" : ""}`}
                onClick={() => toggleStream(s)}
                title="Toggle stream"
              >
                {s}
              </div>
            ))}
          </div>

          <h3>Chart options</h3>
          <div className="row">
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={logScale}
                onChange={(e) => setLogScale(e.target.checked)}
              />
              Log scale
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={showBands}
                onChange={(e) => setShowBands(e.target.checked)}
              />
              Bands (p10–p90)
            </label>
          </div>

          <div className="row">
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={showMA}
                onChange={(e) => setShowMA(e.target.checked)}
              />
              Moving avg
            </label>

            <label>
              Window
              <div>
                <input
                  type="number"
                  min={2}
                  max={60}
                  value={maWindow}
                  onChange={(e) =>
                    setMaWindow(
                      Math.max(
                        2,
                        Math.min(60, parseInt(e.target.value || "7", 10)),
                      ),
                    )
                  }
                  style={{ width: 90 }}
                  disabled={!showMA}
                />
              </div>
            </label>
          </div>

          <div className="row">
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={showOutliers}
                onChange={(e) => setShowOutliers(e.target.checked)}
              />
              Outliers
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={zeroAsNull}
                onChange={(e) => setZeroAsNull(e.target.checked)}
              />
              0 → null
            </label>
          </div>

          <div className="row">
            <label>
              Cumulative
              <div>
                <select
                  value={cumulativeMode}
                  onChange={(e) => setCumulativeMode(e.target.value)}
                >
                  <option value="off">off</option>
                  <option value="on">on</option>
                </select>
              </div>
            </label>
          </div>

          <div className="divider" />

          <div className="actions">
            <button className="btn primary" onClick={load} disabled={loading}>
              {loading ? "Running…" : "Run overlay"}
            </button>
            <span className="pill" title="Backend response time">
              Latency: {latencyMs != null ? `${latencyMs}ms` : "—"}
            </span>
            <span className="pill" title="Last run at">
              Run: {runAt || "—"}
            </span>
          </div>

          {error ? <div className="footnote err">{error}</div> : null}
        </div>

        <div className="content">
          <div className="cards">
            {cards.map((c) => {
              const unit = unitsByStream[c.stream] || "";
              const lastText = definedNum(c.last)
                ? withUnits(c.last, unit)
                : "—";
              const avgText = definedNum(c.avg7)
                ? withUnits(c.avg7, unit)
                : "—";
              const deltaText = definedNum(c.delta) ? pct.format(c.delta) : "—";
              const nextText = definedNum(c.next7)
                ? withUnits(c.next7, unit)
                : "—";
              const deltaCol = signColor(
                definedNum(c.delta) ? (c.delta >= 0 ? 1 : -1) : null,
                dark,
              );

              return (
                <div className="card" key={c.stream}>
                  <h4>{c.stream}</h4>
                  <div className="val">{lastText}</div>
                  <div className="sub">
                    7d avg: <b>{avgText}</b> • Δ:{" "}
                    <b style={{ color: deltaCol }}>{deltaText}</b>
                  </div>
                  <div className="sub">
                    Next 7 (p50 sum): <b>{nextText}</b>
                  </div>
                </div>
              );
            })}
          </div>

          {loading && !data ? (
            <div className="skeleton">Loading overlay…</div>
          ) : (
            <ReactECharts
              ref={chartRef}
              option={option}
              notMerge={true}
              lazyUpdate={true}
              style={{ height: 560, width: "100%", borderRadius: 12 }}
            />
          )}

          <div className="footnote">
            Tip: The URL stays shareable (token/grant preserved). Use{" "}
            <b>Econ & Decisions</b> for PV + shut-in signals + breakevens.
          </div>
        </div>
      </div>
    </div>
  );
}
